import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { focusManager, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth.ts';
import { useRobots, type Robot } from '../hooks/useRobots.ts';
import { useStats } from '../hooks/useStats.ts';
import { usePendingCommands } from '../hooks/useCommands.ts';
import { realtimeConfig } from '../realtime/config.ts';
import { useRealtimeInvalidation } from '../realtime/useRealtimeInvalidation.ts';
import { fleetExceptions, type FleetException } from '../components/fleetMetrics.ts';
import { duration, local, utc, NO_VALUE } from '../components/format.ts';
import { Unit } from '../components/Unit.tsx';
import { LIVENESS_STATE, MATRIX_KEY, STATUS_STATE, type Tone } from '../components/state.ts';
import { useNow } from '../components/useNow.ts';
import { useBrowserRoute } from '../router/useBrowserRoute.ts';
import { buildPath } from '../router/parsePath.ts';
import { Icon } from './Icon.tsx';
import { UserBadge } from './UserBadge.tsx';
import styles from './TopBar.module.css';

/* ==========================================================================
   TopBar — 34px, the height Anduril's Lattice header runs at, and the only
   band of chrome above the workspace rail.

   It answers four questions that no page below it is allowed to leave open:

     WHERE AM I           the wordmark and the deployment environment
     IS ANYTHING WRONG    the fleet's two headline readings — faulted, and
                          out of limits
     IS THE FLEET         how many orders are out and unacknowledged, and how
     LISTENING            long the oldest of them has been waiting
     IS THIS NOW, IS IT   the transport — one mode word (LIVE / POLL / HOLD /
     REAL                 DOWN), the link's median, a Zulu clock, and an alarm
                          slot that names any channel that has stopped
                          answering
     HOW DO I GET THERE   one search affordance, ⌘K, over the real fleet

   ---------------------------------------------------------------------------
   WHAT IS DELIBERATELY NOT ON IT
   ---------------------------------------------------------------------------
   A READ AGE. The surface strip one band below owns freshness — it states it
   in the scope the operator is actually looking at, names the endpoints it is
   stating it for, and carries the re-read verb. A second global age up here
   was the console saying the same thing twice in 20px. What this bar keeps is
   the part the strip cannot see: a source the current surface does not read,
   which is what the alarm slot is for.

   A PER-ROBOT TEXTURE. The previous round drew a 28-cell strip here, one
   column per robot, worst reading wins. It duplicated /fleet's own 6x28
   status matrix 150px below it, in the same order and the same colours, and
   its hover readout opened over the tab strip.

   A FLEET COUNT ANYWHERE ELSE IN THE CHROME. `2 faulted` and `13/28 out of
   limits` are here and nowhere else: not in the nav rail, not in the floor,
   not on the Operations tab.

   Everything on it is a reading. There is no wordmark tagline, no second row,
   no breadcrumb (the surface strip one band below owns page identity) and no
   control that is not either state or an affordance.

   ---------------------------------------------------------------------------
   THE BAR IS A FIXED SLOT GRID
   ---------------------------------------------------------------------------
   Eight fixed tracks, four hairlines and exactly two elastic ones, either side
   of the search control (see TopBar.module.css). The x of everything on this
   bar is a function of the viewport width and of nothing else — not of how
   many robots are faulted, not of how deep the command queue is, not of the
   length of the signed-in address, not of the route. The two fleet readings
   occupy one box whether they read `2 faulted · 13/28 out of limits` or
   `0 faulted · 0/28 out of limits`; the channel alarm is a fixed track whether
   it is dark or naming a dead endpoint; every figure is width-reserved and
   tabular.

   The bar does narrow with WIDTH, at the breakpoints published at the foot of
   TopBar.module.css, and under two rules. First: a word always survives to
   carry the state and a unit always survives to carry the number — `● 2 ● 13`
   is not a state this bar has. Second: what narrows first is
   whatever is SAID TWICE somewhere else on the screen. The operator's local
   wall clock (their OS has one) and the account address (the popover carries
   it in full) go before any measurement does, because a bar that has shed its
   link median has stopped being an instrument.

   ---------------------------------------------------------------------------
   TWO SCOPES OF LIVENESS, ONE COMPUTATION
   ---------------------------------------------------------------------------
   §6 and tell #10 want freshness answered at more than one scope, and
   `inorbit-fleet-status-matrix.png` is the proof: "Fleet Status: Live" and
   "Fleet Log: Live off" sit in the SAME screenshot. Ours are:

     THE LINK, here          ((•)) POLL ▮▮▮▮ 11ms  robots 3s
                             how data is meant to arrive, how good the link
                             carrying it is, and — named — the WORST of the
                             console's six polled sources. §13 tell 27: one
                             light standing in for four channels is a defect,
                             so the slot names the channel rather than always
                             quoting the fleet.

     THE SURFACE, in the     ((•)) Live   read 3s ago  ⟳
     strip's right zone      whether THIS surface's own sources are still
                             arriving, and how stale its stalest one is.

     THE PANEL, in a         components/Freshness' LiveTag in Panel's `meta`.
     panel header

   Three scopes, but ONE computation: `SOURCES`, `readOf` and `evaluate` below
   are the only place in the console that decides what "late" means, and the
   strip reads them through `useSurfaceRead`. The last round had two of these
   scopes computed independently and they disagreed on screen — a calm `POLL`
   over a green `LIVE`. They cannot now: they are the same function.

   Everything a native tooltip used to hide — why push is unavailable, what
   the fallback is, each source's interval, and the last successful read as an
   absolute Zulu stamp — is in an on-screen popover behind a click (§13,
   tell 13: a link readout with no distribution behind it is a defect).
   ========================================================================== */

/* --------------------------------------------------------------------------
   THE HOLD — `Live` ⇄ `Live off`, and it is a switch, not a light.

   An operator reading row 40 of a table that re-sorts under them every five
   seconds needs to be able to stop time, and every reference in the set gives
   them a way: Gotham pairs its scrubber with an explicit `Go Live`, Formant's
   `LIVE` pill greys the instant you scrub off live, InOrbit flips a panel
   from `((•)) Live` to `((x)) Live off`. A liveness indicator that is only
   ever an indicator cannot be any of those.

   The hold is real and it is the framework's own switch: TanStack gates every
   `refetchInterval` on `focusManager.isFocused()`, so setting that false stops
   every poll in the console at source rather than layering a second timer over
   the top. Releasing sets it back to `undefined` — automatic — rather than to
   `true`, so a genuinely backgrounded tab goes back to stopping its own timers.

   What the hold deliberately does NOT do is freeze the age. The ages keep
   counting and escalate exactly as they would if the link had failed, because
   "I am holding" and "this is four minutes old" are both true and the operator
   needs the second one to decide whether to trust what is on screen.
   -------------------------------------------------------------------------- */

let heldValue = false;
const holdListeners = new Set<() => void>();

function getHeld(): boolean {
  return heldValue;
}

function subscribeHold(listener: () => void): () => void {
  holdListeners.add(listener);
  return () => {
    holdListeners.delete(listener);
  };
}

function setHeld(next: boolean): void {
  if (heldValue === next) return;
  heldValue = next;
  focusManager.setFocused(next ? false : undefined);
  holdListeners.forEach((l) => l());
}

/* The two hooks below are exported from a file that also exports a component,
   which costs this module its fast-refresh boundary in dev. That is the right
   trade: the alternative is a second copy of `SOURCES`, `readOf` and
   `evaluate` living beside the surface strip, and the whole point of the round
   is that the console's liveness scopes are ONE computation. A dev-only HMR
   nicety does not get to fork the definition of "late".
   ------------------------------------------------------------------------ */

/**
 * Is the operator typing into something?
 *
 * Every BARE-key shortcut in the console is gated on this — `/` here, `L` in
 * the shell's surface strip. One definition, exported, because two of them
 * would drift and the failure mode is a filter field that eats a letter.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/** Is the console holding its polls? Shared by the strip and this bar. */
// eslint-disable-next-line react-refresh/only-export-components
export function useLiveHold(): { held: boolean; setHeld: (next: boolean) => void } {
  const held = useSyncExternalStore(subscribeHold, getHeld, getHeld);
  return { held, setHeld };
}

/* --------------------------------------------------------------------------
   THE SUPPLY STORE — one answer to "is anything arriving", published once.

   The shell's status bar spells this out in its bottom-left corner
   (`palantir-gotham-map-workspace.png`'s `Map health — Connected`, which is
   §6's "ambient connection truth in a fixed corner"). The word up here in the
   34px band is the four-glyph mode; the floor 22px off the other edge of the
   screen is the same fact spelled out beside the tenant and the host it is a
   fact ABOUT.

   Two bands, one computation — and it has to be published rather than
   recomputed, because the mode depends on `useRealtimeInvalidation`, which
   opens the MQTT socket. A second caller would be a second subscription to
   the tenant's whole fleet subtree. So `Transport` computes it once, writes
   it here, and the floor reads it.
   -------------------------------------------------------------------------- */

export interface Supply {
  /** live = push is up · poll = REST is carrying it · held · down. */
  state: keyof typeof SUPPLY_WORD;
  /** The floor's spelling: `mqtt push`, `rest polling`, `polling held`. */
  phrase: string;
  tone: Tone;
}

let supplyValue: Supply = { state: 'poll', phrase: 'rest polling', tone: 'ok' };
const supplyListeners = new Set<() => void>();

function getSupply(): Supply {
  return supplyValue;
}

function subscribeSupply(listener: () => void): () => void {
  supplyListeners.add(listener);
  return () => {
    supplyListeners.delete(listener);
  };
}

function publishSupply(next: Supply): void {
  const prev = supplyValue;
  if (prev.state === next.state && prev.phrase === next.phrase && prev.tone === next.tone) return;
  supplyValue = next;
  supplyListeners.forEach((l) => l());
}

/** How data is arriving right now. Written by `Transport`, read by the floor. */
// eslint-disable-next-line react-refresh/only-export-components
export function useSupply(): Supply {
  return useSyncExternalStore(subscribeSupply, getSupply, getSupply);
}

/**
 * ONE WORD FOR "THIS IS CURRENT", AND THE TRANSPORT PICKS IT.
 *
 * A `/fleet` frame used to name one fact four ways: `POLL` in the mode pill,
 * `Live` on the surface strip, `Polling 5s` on the eight panel headers 100px
 * below it, and `rest polling` on the floor. The mode pill and the floor are
 * two registers of the same word and are fine; the strip and the panels were
 * two different words for the same state, with the same 6px dot in front of
 * each, adjacent on screen.
 *
 * So the freshness word is named after the transport that is actually feeding
 * the panel — `Live` only while push is genuinely up, `Polling` while REST is
 * carrying it, which is every route today because MQTT is gated on config the
 * console does not have. One word per frame, and it changes on its own when
 * the transport does.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useFeedWord(): 'Live' | 'Polling' {
  return useSupply().state === 'live' ? 'Live' : 'Polling';
}

/**
 * Is the document visible?
 *
 * Deliberately NOT `focusManager.isFocused()` any more: the hold above drives
 * that, so asking it "is anyone looking at this" would answer "no" the moment
 * an operator paused on purpose — and every age would stop escalating exactly
 * when it most needs to.
 */
function useDocumentVisible(): boolean {
  return useSyncExternalStore(
    (listener) => {
      document.addEventListener('visibilitychange', listener);
      return () => document.removeEventListener('visibilitychange', listener);
    },
    () => document.visibilityState !== 'hidden',
    () => true,
  );
}

/**
 * The browser's own verdict on whether this machine has a network at all.
 *
 * It is the first question of any outage triage and the only one the console
 * can answer without the platform: `offline` means the operator's laptop lost
 * its link and every red row below it is a consequence, not a cause. It is
 * deliberately reported as the browser's word rather than inferred from failed
 * reads — a captive portal and a dead API produce identical `Failed to fetch`
 * strings, and this is the one signal that tells them apart.
 */
function useOnline(): boolean {
  return useSyncExternalStore(
    (listener) => {
      window.addEventListener('online', listener);
      window.addEventListener('offline', listener);
      return () => {
        window.removeEventListener('online', listener);
        window.removeEventListener('offline', listener);
      };
    },
    () => (typeof navigator === 'undefined' ? true : navigator.onLine),
    () => true,
  );
}

/* --------------------------------------------------------------------------
   Deployment environment — read off the origin, never configured by hand.
   An operator must never have to guess which plane they are driving.
   -------------------------------------------------------------------------- */

type EnvId = 'local' | 'dev' | 'staging' | 'prod';

function environment(): { id: EnvId; label: string } {
  const host = typeof window === 'undefined' ? '' : window.location.hostname;
  if (
    import.meta.env.DEV ||
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '[::1]' ||
    host.endsWith('.local')
  ) {
    return { id: 'local', label: 'Local' };
  }
  if (/(^|[.-])(dev|develop)([.-]|$)/.test(host)) return { id: 'dev', label: 'Dev' };
  if (/(^|[.-])(staging|stg)([.-]|$)/.test(host)) return { id: 'staging', label: 'Staging' };
  return { id: 'prod', label: 'Prod' };
}

/* --------------------------------------------------------------------------
   THE PALETTE CHORD, SPELLED FOR THE MACHINE IT IS PRINTED ON.

   §6: "shortcuts are printed in the affordance". A shortcut printed as `⌘K`
   on a Linux workstation is not a shortcut, it is a glyph the operator cannot
   press — and half this fleet's operators are on Linux boxes beside the
   robots. The handler already accepts either modifier (see TopBar); this is
   the label agreeing with it.

   `userAgentData.platform` where the browser has it, `navigator.platform`
   where it does not — deprecated, still universally implemented, and the only
   thing that answers this question without a UA-string parse.
   -------------------------------------------------------------------------- */
const APPLE = ((): boolean => {
  if (typeof navigator === 'undefined') return true;
  const nav = navigator as Navigator & { userAgentData?: { platform?: string } };
  const p = nav.userAgentData?.platform ?? navigator.platform ?? '';
  return /mac|iphone|ipad|ipod/i.test(p);
})();

/** What the ⌘K affordance prints, and what its tooltip says. */
const CHORD = APPLE ? '⌘K' : 'Ctrl K';

/* The operator's own wall clock, named. Zulu is the console's time; this is
   the offset they have to do the arithmetic against, so it is printed once. */
const TZ_LABEL = ((): string => {
  try {
    const parts = new Intl.DateTimeFormat(undefined, {
      timeZoneName: 'short',
    }).formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
})();

/** An exception's reasons, as one line: `BATT 8% · SEEN 19s`. */
function reasonLine(e: FleetException): string {
  return e.reasons.map((r) => `${r.label.toUpperCase()} ${r.text}`).join(' · ');
}

/* --------------------------------------------------------------------------
   Fleet rollup.

   Severity comes straight from `fleetExceptions`, which is the same function
   the exception table on /fleet uses, so the chrome and the page cannot
   disagree about who is in trouble — they are the same computation.
   -------------------------------------------------------------------------- */

const TONE_BY_SEVERITY: readonly Tone[] = ['warn', 'maintenance', 'offline', 'error'];

/* ==========================================================================
   THE TWO FLEET NOUNS — one definition each, and every band reads them here.

   This is the fix for the defect that failed the last round. In a single
   1600x1000 frame of /fleet the console said `4 fault` in the top bar,
   `FAULTED 2/28` in the KPI strip 130px below it and `2 faulted` in the floor
   at the other bezel — three figures, two definitions, one noun. And the
   identical quantity 13/28 was painted `--error-fg` in the nav rail and
   `--warn-fg` in the KPI 80px away.

   The console now has exactly two fleet-wide readings, and both are stated in
   the page's own words, with the page's own thresholds and the page's own
   colour, in every band that carries them:

     FAULTED         `status === 'error'` — the robot is refusing motion.
                     RED, because §3 spends red on "a machine is down" and on
                     nothing else. Also: workspaces/fleet/views/Dashboard's
                     `faulted` (statusCounts.get('error')) and its `FAULTED
                     2/28 · refusing motion` card.
     OUT OF LIMITS   `fleetExceptions(...).length` — the robot carries at
                     least one reading outside its published limit, of which
                     "refusing motion" is one. AMBER, never red: a robot on
                     36% charge needs a human this shift, not this minute.
                     Also: Dashboard's `NEEDS ATTENTION 13/28 · readings out
                     of limits`, the surface strip's `Operations 13/28` and
                     the nav rail's `13/28`.

   Faulted is a SUBSET of out-of-limits — a robot with `status === 'error'`
   always carries a MODE fault row in `fleetExceptions` — so the pair reads as
   a whole and its worst part, never as two overlapping tallies.

   What is deliberately NOT on the bar any more is the four-way split of the
   exception set by worst severity (`4 fault · 1 no data · 1 held · 7 not
   current`). Those four are the STATUS MATRIX's tone vocabulary; they are
   correct there, beside the texture they label, and printing them in 34px of
   global chrome put the word `fault` on a count of 4 twenty pixels above a
   card reading `FAULTED 2`. The split stays where it can be read against its
   own legend: the census on /fleet, and this control's own title and
   screen-reader label, both of which enumerate it in full.
   ========================================================================== */

export interface FleetHeadline {
  /** Robots on the books. */
  total: number;
  /** Carrying at least one reading outside its limits. Amber. */
  flagged: number;
  /** `status === 'error'` — refusing motion. Red. A subset of `flagged`. */
  faulted: number;
}

/**
 * The console's two fleet nouns, computed once.
 *
 * Exported because the top bar, the nav rail, the surface strip's tab counts
 * and the floor's ledger all print one or both of them, and four call sites
 * with four inline predicates is exactly how a console ends up contradicting
 * itself inside one frame.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function fleetHeadline(robots: readonly Robot[]): FleetHeadline {
  let faulted = 0;
  for (const r of robots) if (r.status === 'error') faulted += 1;
  return { total: robots.length, flagged: fleetExceptions(robots).length, faulted };
}

/* --------------------------------------------------------------------------
   ONE FACT, ONE HOME — why there is no per-robot texture up here any more.

   The previous round drew a 28-cell strip in this cluster: one column per
   robot, worst reading wins, in the fleet's own order. It was the fastest
   reading on the bar and it was also the SECOND drawing of exactly the same
   28 robots in exactly the same order on /fleet, 150px above the `Fleet
   status` matrix that draws them six metrics deep — and its hover readout
   opened over the tab strip, covering the route the operator was reading.

   A console earns trust by saying each thing once, in the band that owns it.
   The matrix owns per-robot texture; it is bigger, deeper, labelled and it
   scrolls with the surface. What the chrome owes an operator on /telemetry or
   /catalog — where no matrix exists — is not a smaller copy of it, it is the
   ROLLUP: how many machines are down, and how many are outside a limit. Those
   two figures are what this cluster is now, and nothing else.
   -------------------------------------------------------------------------- */

function FleetCounts({ onOpen }: { onOpen: (path: string) => void }) {
  const { data, dataUpdatedAt, errorUpdatedAt } = useRobots();
  /* Three states, not two. `stale` is a picture we no longer trust; `unread`
     is no picture at all, and the difference matters — a console that renders
     "0 faulted" when it could not reach the API has told the operator the
     fleet is healthy on the strength of a failed request.

     Derived from the two update stamps rather than from `isPending`/`isError`,
     which both drop out for the length of every retry on a query that has
     never had data (see `isFailing`). */
  const unread = data === undefined;
  const stale = data !== undefined && errorUpdatedAt > 0 && errorUpdatedAt > dataUpdatedAt;
  const robots = useMemo(() => data ?? [], [data]);

  /* The two nouns, from the one definition — see THE TWO FLEET NOUNS. */
  const head = useMemo(() => fleetHeadline(robots), [robots]);

  /* The four-way split of the exception set by each robot's WORST reading.
     Not printed on the bar — that is the status matrix's own vocabulary and
     it is drawn beside its legend on /fleet — but carried in this control's
     title and screen-reader label, because §3 forbids colour as the sole
     carrier and an operator who wants the decomposition should not have to
     change route to get it. */
  const split = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const e of fleetExceptions(robots)) {
      const tone = TONE_BY_SEVERITY[e.severity] ?? 'warn';
      counts[tone] = (counts[tone] ?? 0) + 1;
    }
    return (['error', 'offline', 'maintenance', 'warn'] as const)
      .map((tone) => ({
        n: counts[tone] ?? 0,
        label: MATRIX_KEY.find((k) => k.tone === tone)?.label ?? tone,
      }))
      .filter((c) => c.n > 0);
  }, [robots]);

  /* Read is `null` until the fleet has actually been read once. Nothing about
     a fleet we have not reached is zero. */
  const read = unread ? null : true;

  /* ---- the alarm ----------------------------------------------------------
     §7: "a new fault may pulse its row background twice over ~1s, then stop
     and stay statically red. A permanently blinking alarm is banned."

     This is the console's only alarm surface — `faulted` is the one figure on
     the bar that means a machine has just gone down — and until now it
     changed from `1` to `2` with no event at all: an operator reading a
     table two bands below would never know. It pulses on a RISE only (a fleet
     recovering is not an alarm), twice, and then holds. `prefers-reduced-
     motion` collapses the animation globally (styles.css), so the escalation
     an operator who asked for less motion sees is the figure itself changing
     colour, which it already does. */
  const faults = read === null ? null : head.faulted;
  const [alarm, setAlarm] = useState(false);
  const lastFaults = useRef<number | null>(null);
  useEffect(() => {
    const prev = lastFaults.current;
    lastFaults.current = faults;
    if (faults === null || prev === null || faults <= prev) return;
    setAlarm(true);
    const t = window.setTimeout(() => setAlarm(false), 1_200);
    return () => window.clearTimeout(t);
  }, [faults]);

  const spoken =
    split.length === 0
      ? 'every robot inside its limits'
      : split.map((c) => `${c.n} ${c.label}`).join(', ');
  const label =
    read === null
      ? 'Fleet has not been read. Open fleet operations.'
      : `Fleet: ${head.total} robots, ${head.faulted} faulted, ${head.flagged} with a reading out of limits — worst reading per robot: ${spoken}. Open fleet operations.`;

  /* THE TWO READINGS. Fixed box, fixed order, one definition each — see THE
     TWO FLEET NOUNS above. `2 faulted` is the same set and the same red as
     the KPI card `FAULTED 2/28`; `13/28 out of limits` is the same set and
     the same amber as the KPI card `NEEDS ATTENTION 13/28`. This is the
     console's ONLY always-on fleet reading: the floor carries no counts and
     the nav rail carries no counts, so nothing on the chrome can be read
     against a different figure wearing the same noun.

     Both entries are always drawn, whether or not they are carrying anything,
     because `0 faulted` and `2 faulted` must occupy the same box — the ⌘K
     control does not move because the fleet recovered. A zero is a READING
     and prints as a dim `0`; `—` is reserved for "we could not ask".

     One control, one tab stop, one destination: /fleet, where both figures
     are decomposed against their own legend. */
  return (
    <button
      type="button"
      className={styles.counts}
      data-stale={stale}
      onClick={() => onOpen(buildPath('fleet'))}
      aria-label={stale ? `Fleet read failed. Last known: ${label}` : label}
      title={
        read === null
          ? 'The fleet could not be read — nothing here is a reading'
          : `${stale ? 'Fleet read failed — this is the last picture we had.\n' : ''}${
              head.faulted
            } of ${head.total} robots are refusing motion (status: error).\n${
              head.flagged
            } of ${head.total} carry at least one reading outside its limits — worst reading per robot: ${spoken}.\nOpen fleet operations.`
      }
    >
      <span
        className={styles.count}
        data-tone="error"
        data-zero={read !== null && head.faulted === 0}
        data-unread={read === null}
        data-alarm={alarm}
      >
        <span className={styles.countNum}>
          <span className={styles.countLead}>{read === null ? NO_VALUE : head.faulted}</span>
        </span>
        <span className={styles.countLong}>faulted</span>
        <span className={styles.countShort}>faulted</span>
      </span>

      <span
        className={styles.count}
        data-tone="warn"
        data-zero={read !== null && head.flagged === 0}
        data-unread={read === null}
      >
        {/* The numerator is padded to the denominator's own width with
            U+2007 FIGURE SPACE — one tabular digit — so ` 0/28` and `13/28`
            are the same box and a recovering fleet does not slide the word
            beside it. The same string, character for character, as the
            Operations surface prints in its own KPI. */}
        <span className={styles.countNum} data-frac="true">
          <span className={styles.countLead}>
            {read === null
              ? NO_VALUE
              : String(head.flagged).padStart(String(head.total).length, '\u2007')}
          </span>
          /{read === null ? NO_VALUE : head.total}
        </span>
        <span className={styles.countLong}>out of limits</span>
        <span className={styles.countShort}>out of lim.</span>
      </span>
    </button>
  );
}
/* --------------------------------------------------------------------------
   Commands out and unacknowledged.

   An operator reading telemetry or editing the catalog still needs to know
   that sixteen orders are in flight, and the bar is the only place they would
   see it. Deliberately uncoloured — a deep queue is not by itself a fault, and
   this console does not yet know how long any one command has been waiting
   (see the report).

   THE WORD IS `unacked`, NOT `queued`, AND THAT IS A DEFINITION, NOT A STYLE
   CHOICE. This figure is `/api/stats.pending_commands`, which the API defines
   as `pending + sent` — issued, not yet acknowledged by a robot. The Commands
   panel on /fleet counts a WIDER set from a different endpoint — `pending +
   sent + acked`, the whole open queue off the per-robot ledger — and prints it
   as `Queue 26`. Two honest numbers over two different sets, and the last
   round wore one noun (`16 queued` up here, `Queue 26` down there) over both.
   The noun now carries the difference: `16 unacked` is a strict subset of
   `Queue 26`, and the ten in between are acknowledged and executing.

   Same word, same set, on the nav rail's Commands cell and on the /commands
   floor, which read the same endpoint.
   -------------------------------------------------------------------------- */

function Queue({ onOpen }: { onOpen: (path: string) => void }) {
  const { data, dataUpdatedAt, errorUpdatedAt } = useStats();
  const n = data?.pending_commands;

  /* ---- HOW LONG THE HEAD OF THE QUEUE HAS BEEN WAITING -------------------
     §13 tell 24: a figure titled "current" that does not say how current.
     `16 unacked` is not a judgement — sixteen orders issued in the last
     twenty seconds is a busy shift and sixteen that have been sitting for
     forty minutes is a fleet that has stopped listening, and until now this
     bar printed the same string for both. The queue's OLDEST issue time is
     the reading that separates them, and it is the one an operator about to
     issue another command actually needs.

     Same list, same endpoint the count is defined against — `/api/commands/
     pending` is `pending + sent`, so the age describes exactly the set the
     number beside it counts. Read here rather than derived from whatever the
     open workspace happens to have cached, so it is the same reading on every
     route; TanStack dedupes it against /commands' own read of the same key,
     and the console's hold stops it with everything else (see THE HOLD). */
  const pending = usePendingCommands();
  const now = useNow(true);

  /* Same latch as everywhere else on this bar — which terminal event was last,
     not `isError`, which query-core clears at the top of every retry (see
     `isFailing`). The control dims if EITHER of its two sources is failing:
     it is one control with one hairline, and a bright age over a dead
     `/api/commands/pending` is the same lie as a bright count over a dead
     `/api/stats`. Which endpoint broke is named in the strip's fault row and
     in the transport popover, where an operator can act on it. */
  const isError =
    (errorUpdatedAt > 0 && errorUpdatedAt > dataUpdatedAt) ||
    (pending.errorUpdatedAt > 0 && pending.errorUpdatedAt > pending.dataUpdatedAt);

  const oldestMs = useMemo(() => {
    const rows = pending.data;
    if (rows === undefined || rows.length === 0) return null;
    let oldest = Number.POSITIVE_INFINITY;
    for (const c of rows) {
      const t = Date.parse(c.issued_at);
      if (Number.isFinite(t) && t < oldest) oldest = t;
    }
    return Number.isFinite(oldest) ? Math.max(0, now - oldest) : null;
  }, [pending.data, now]);

  /* Deliberately uncoloured. The platform publishes no acknowledgement
     deadline — there is no field on a command that says when it should have
     been picked up — and a threshold this console invented would be a limit
     an operator learns to trust and nobody agreed to. §13 tell 15 wants
     thresholds drawn rather than remembered; the honest answer where the API
     has none is to print the measurement and say so in the report. */
  const ageLabel = oldestMs === null ? NO_VALUE : duration(oldestMs, { compact: 1 });
  const spokenAge =
    n === 0
      ? 'nothing waiting'
      : oldestMs === null
        ? 'the queue has not been read'
        : `the oldest was issued ${duration(oldestMs)} ago`;

  return (
    <button
      type="button"
      className={styles.queue}
      data-stale={isError}
      onClick={() => onOpen(buildPath('commands'))}
      title={
        isError
          ? 'Fleet statistics unavailable'
          : `${n ?? 0} command${n === 1 ? '' : 's'} issued and not yet acknowledged by a robot (pending or sent) — /api/commands/pending — and ${spokenAge}. Open the queue.`
      }
      aria-label={`${n ?? 0} commands issued and not yet acknowledged, ${spokenAge}. Open the command queue.`}
    >
      <span
        className={styles.queueNum}
        data-idle={n === 0}
        /* A hole, at the disabled step — not a bright `—`. See the note beside
           `.count[data-unread]`: this console has one rendering of "we could
           not ask" and it is never a status colour and never the brightest
           character in its cluster. */
        data-unread={n === undefined}
      >
        {n === undefined ? NO_VALUE : n}
      </span>
      <span className={styles.queueLabel}>unacked</span>
      {/* The word before the number, not after it: `oldest 24m` cannot be
          mistaken for the read age in the transport cluster the way a bare
          `24m` on a bar full of ages could. It sheds below 1480px with the
          wall clock, and the count never does. */}
      <span className={styles.queueAge}>
        <span className={styles.queueAgeLead}>oldest</span>
        <span className={styles.queueAgeValue} data-idle={oldestMs === null}>
          {ageLabel}
        </span>
      </span>
    </button>
  );
}

/* --------------------------------------------------------------------------
   Transport — the console's single liveness statement.

   Four words, because colour is never the sole carrier:

     LIVE   MQTT push is connected on this tenant's fleet subtree
     POLL   push is absent or down; REST polling is carrying it, on a timer
     HELD   the operator stopped every timer in the console
     DOWN   reads are failing — nothing is arriving at all

   The word says whether anything is ARRIVING. Which plane it is arriving on,
   and why the other one is not being used, is one click away in the popover —
   a distinction that matters to whoever is fixing the deployment and not at
   all to the operator glancing at the corner. The ages beside it say how old
   the picture is, one per source, named, because "the link is up" and "the
   fleet list is 40 seconds old" are different facts and an operator who is
   about to issue a command needs both.

   The whole control is fixed-width: the pill, the meter and its median, and
   one named slot carrying the worst source the console is currently reading.
   The mode word therefore sits at the same x on every route and never has to
   be found again, and the named slot is sized to the longest source label the
   console has rather than to whichever one is broken today.
   -------------------------------------------------------------------------- */

/** A polled REST family, named the way an operator would name it. */
interface SourceSpec {
  id: string;
  label: string;
  prefix: readonly unknown[];
  /** The hook's own declared cadence. 0 = read on demand, never polled. */
  everyMs: number;
  path: string;
  /**
   * Which request URLs belong to this family, so the browser's own resource
   * timings can be attributed back to the source that made them (see THE LINK
   * WINDOW). Matched against the pathname only, in declared order.
   */
  match: RegExp;
}

/* Cadences are the ones the hooks actually set (hooks/useRobots, useStats,
   useTelemetry, useCommands, useScans, useCatalog). If a hook's interval
   changes and this table does not, the popover is lying — so it prints the
   interval next to the age it produced, where a mismatch is visible. */
const SOURCES: readonly SourceSpec[] = [
  {
    id: 'robots',
    label: 'robots',
    prefix: ['robots'],
    everyMs: 5_000,
    path: '/api/robots',
    /* The list and a single robot. Deliberately NOT `/api/robots/…/anything`:
       a sub-resource belongs to the family that reads it, below. */
    match: /^\/api\/robots(\/[^/]+)?$/,
  },
  {
    id: 'stats',
    label: 'stats',
    prefix: ['stats'],
    everyMs: 5_000,
    path: '/api/stats',
    match: /^\/api\/stats$/,
  },
  {
    id: 'telemetry',
    label: 'telemetry',
    prefix: ['telemetry'],
    everyMs: 5_000,
    path: '/api/robots/…/telemetry',
    match: /^\/api\/robots\/[^/]+\/telemetry$/,
  },
  {
    id: 'commands',
    label: 'commands',
    prefix: ['commands', 'pending'],
    everyMs: 3_000,
    path: '/api/commands/pending',
    match: /^\/api\/(commands\/pending|robots\/[^/]+\/commands)$/,
  },
  {
    id: 'scans',
    label: 'scans',
    prefix: ['scans'],
    everyMs: 15_000,
    path: '/api/robots/…/scans',
    match: /^\/api\/(scans\/[^/]+|robots\/[^/]+\/scans)$/,
  },
  {
    id: 'catalog',
    label: 'catalog',
    prefix: ['morphologies'],
    everyMs: 0,
    path: '/api/morphologies',
    match: /^\/api\/morphologies/,
  },
];

const SOURCE_BY_ID = new Map(SOURCES.map((s) => [s.id, s]));

/* ==========================================================================
   THE LINK WINDOW — link quality as a MEASUREMENT, not an assertion.

   §13 tell 13 is blunt: "a latency number with no distribution behind it" is
   a defect, and so is a bar that reports only whether reads are arriving. The
   last round's transport cluster answered `POLL … fleet 3s` — how data is
   meant to arrive, and how old the picture is. Both true, and neither of them
   says whether the link is HEALTHY. A console can be three seconds fresh over
   a link that is taking 900ms a read and about to fall over, and this bar had
   no way to say so.

   Nothing here is invented. Every figure is read off `PerformanceResourceTiming`
   — the browser's own record of requests this document actually made — so the
   numbers are the ones the operator's own machine measured:

     ROUND TRIP   `responseEnd - requestStart`: headers out to last byte in.
                  Connection setup and queueing are deliberately EXCLUDED, so
                  the figure is about the API rather than about how many
                  requests a workspace happened to fire at once.
     OUTCOME      `responseStatus`, so a 503 is counted as a failure and never
                  as a fast read. A transport-level failure (a dead socket, a
                  blocked request) lands as an entry with `responseStatus: 0`
                  and `responseStart: 0` — 0.7ms of nothing — and folding that
                  into the median would make an outage look like the fastest
                  link the console has ever had.
     SIZE         `transferSize`, headers included: what the link actually
                  carried, not what the JSON decoded to.
     SOURCE       each URL is attributed back to its `SourceSpec`, so the
                  popover can say WHICH family is slow. §13 tell 27 — one
                  global connection light standing in for four channels is a
                  defect; `skydio-remote-flight-deck-live.png` runs three
                  meters side by side for exactly this reason.

   Two minutes of window, because at a 5s poll that is ~24 fleet reads: enough
   for a median to mean something, short enough that recovery shows up while
   the operator is still looking at it. The window is named on screen.

   The observer is a module singleton with `buffered: true`, so it also picks
   up everything that happened before the first mount, and it keeps its own
   ring rather than reading `performance.getEntriesByType` — that buffer is
   capped at 250 entries by the platform and a console at five polling
   families overruns it in under a minute.
   ========================================================================== */

/** How far back the link window looks. ~24 fleet reads at a 5s poll. */
const LINK_WINDOW_MS = 120_000;
/** Ring cap — about four windows of a busy surface. */
const LINK_CAP = 512;

/**
 * The published grade ladder, in ms of round trip. Printed in the popover:
 * §13 tell 15 is that a threshold which exists only in the operator's head is
 * a defect, so the numbers the meter is drawn against are on screen beside it.
 */
const LINK_LADDER = [50, 150, 400] as const;

interface LinkSample {
  /** Epoch ms the response finished landing. */
  t: number;
  /** Round trip in ms. Null = the read never produced a response. */
  ms: number | null;
  /** Bytes off the wire, headers included. */
  bytes: number;
  /** The `SourceSpec.id` this URL belongs to, or null for an unnamed read. */
  src: string | null;
}

const linkRing: LinkSample[] = [];
const linkListeners = new Set<() => void>();
let linkSerial = 0;
let linkStarted = false;
/** False where the platform gives us no resource timings to read. */
let linkSupported = true;

function sourceOfPath(pathname: string): string | null {
  for (const s of SOURCES) if (s.match.test(pathname)) return s.id;
  return null;
}

function startLinkWindow(): void {
  if (linkStarted) return;
  linkStarted = true;
  if (typeof PerformanceObserver === 'undefined' || typeof performance === 'undefined') {
    linkSupported = false;
    return;
  }

  const origin = performance.timeOrigin;
  const take = (entries: readonly PerformanceEntry[]) => {
    let added = 0;
    for (const entry of entries) {
      const r = entry as PerformanceResourceTiming;
      if (r.initiatorType !== 'fetch' && r.initiatorType !== 'xmlhttprequest') continue;
      let url: URL;
      try {
        url = new URL(r.name, window.location.href);
      } catch {
        continue;
      }
      /* SAME ORIGIN ONLY. A cross-origin response with no `Timing-Allow-Origin`
         reports `requestStart`, `responseEnd`, `responseStatus` and
         `transferSize` as zero — indistinguishable from a dead socket — and
         measuring it would paint a healthy link as total packet loss. Every
         call in this build goes to a relative `/api/…` (auth/apiFetch), so
         this excludes nothing today and refuses to lie if that ever changes. */
      if (url.origin !== window.location.origin) continue;
      const pathname = url.pathname;
      if (!pathname.startsWith('/api/')) continue;

      /* `responseStatus` is Chromium/Firefox and recent Safari. Where it is
         absent, "did any response byte arrive" is the honest fallback: it
         cannot separate a 503 from a 200, but it never reports a dead socket
         as a fast read, which is the failure that would matter. */
      const status =
        typeof r.responseStatus === 'number' ? r.responseStatus : r.responseStart > 0 ? 200 : 0;
      const start = r.requestStart > 0 ? r.requestStart : r.startTime;
      const ok = status >= 200 && status < 400 && r.responseEnd > start;

      linkRing.push({
        t: origin + r.responseEnd,
        ms: ok ? r.responseEnd - start : null,
        bytes: ok ? r.transferSize : 0,
        src: sourceOfPath(pathname),
      });
      added += 1;
    }
    if (added === 0) return;
    if (linkRing.length > LINK_CAP) linkRing.splice(0, linkRing.length - LINK_CAP);
    linkSerial += 1;
    for (const fn of linkListeners) fn();
  };

  try {
    const obs = new PerformanceObserver((list) => take(list.getEntries()));
    obs.observe({ type: 'resource', buffered: true });
  } catch {
    /* No resource-timing support: the meter renders `—` and the popover says
       why, rather than showing an empty meter that reads as a dead link. The
       console is not degraded by this — the mode word and the ages beside it
       are unaffected. */
    linkSupported = false;
  }
}

function subscribeLink(listener: () => void): () => void {
  startLinkWindow();
  linkListeners.add(listener);
  return () => {
    linkListeners.delete(listener);
  };
}

function getLinkSerial(): number {
  return linkSerial;
}

interface LinkStats {
  /** Reads in the window that produced a response. */
  ok: number;
  /** Reads in the window that did not. */
  failed: number;
  /** Median round trip in ms. Null = nothing to measure yet. */
  p50: number | null;
  min: number | null;
  max: number | null;
  /** Standard deviation of the round trip, in ms. */
  jitter: number | null;
  /** Bytes off the wire across the window. */
  bytes: number;
  /** Per-source volume, loss and median, for the popover's own column. */
  bySource: ReadonlyMap<string, { ok: number; failed: number; p50: number | null }>;
}

const LINK_EMPTY: LinkStats = {
  ok: 0,
  failed: 0,
  p50: null,
  min: null,
  max: null,
  jitter: null,
  bytes: 0,
  bySource: new Map(),
};

function median(sorted: readonly number[]): number {
  const n = sorted.length;
  const mid = n >> 1;
  return n % 2 === 1 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

function computeLink(now: number): LinkStats {
  const from = now - LINK_WINDOW_MS;
  const all: number[] = [];
  const per = new Map<string, { ms: number[]; failed: number }>();
  let failed = 0;
  let bytes = 0;

  for (const s of linkRing) {
    if (s.t < from) continue;
    const bucket =
      s.src === null
        ? null
        : (per.get(s.src) ?? (() => {
            const fresh = { ms: [] as number[], failed: 0 };
            per.set(s.src, fresh);
            return fresh;
          })());
    if (s.ms === null) {
      failed += 1;
      if (bucket) bucket.failed += 1;
      continue;
    }
    all.push(s.ms);
    bytes += s.bytes;
    if (bucket) bucket.ms.push(s.ms);
  }

  const bySource = new Map<string, { ok: number; failed: number; p50: number | null }>();
  for (const [id, b] of per) {
    b.ms.sort((a, c) => a - c);
    bySource.set(id, {
      ok: b.ms.length,
      failed: b.failed,
      p50: b.ms.length === 0 ? null : median(b.ms),
    });
  }

  if (all.length === 0) return { ...LINK_EMPTY, failed, bySource };

  all.sort((a, b) => a - b);
  const mean = all.reduce((t, v) => t + v, 0) / all.length;
  const variance = all.reduce((t, v) => t + (v - mean) * (v - mean), 0) / all.length;

  return {
    ok: all.length,
    failed,
    p50: median(all),
    min: all[0]!,
    max: all[all.length - 1]!,
    jitter: Math.sqrt(variance),
    bytes,
    bySource,
  };
}

/**
 * The link window, recomputed once a second off the shell's own clock. The
 * subscription is to the observer (a read landed) and the `now` dependency is
 * what retires samples out of the trailing edge of the window.
 */
function useLinkStats(now: number): LinkStats {
  const serial = useSyncExternalStore(subscribeLink, getLinkSerial, getLinkSerial);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => computeLink(now), [serial, now]);
}

/* --------------------------------------------------------------------------
   THE ATTEMPT STREAM — every read this document made, at its true timestamp.

   `formant-timeline-data-presence-scrubber.png` encodes data availability as
   a solid presence band with white gaps where no data exists, so the absence
   is visible BEFORE you scrub into it. The same idea, turned on the console's
   own reads: one tick per request, at its real x on a two-minute ruler, per
   source.

   It answers the question the ledger's `4 in a row` cannot — whether an
   endpoint is DEAD or FLAPPING. A run of red with no gaps is an outage; red
   and steel alternating is a link losing a third of its packets, and the two
   have completely different next actions. Nothing here is derived: these are
   the browser's own `PerformanceResourceTiming` entries, the same ring the
   link meter's median is computed from.
   -------------------------------------------------------------------------- */

export interface ReadAttempt {
  /** Epoch ms the response finished landing (or failed to). */
  t: number;
  ok: boolean;
}

export interface AttemptWindow {
  from: number;
  to: number;
  /** Attempts inside the window, per `SourceSpec.id`, oldest first. */
  bySource: ReadonlyMap<string, readonly ReadAttempt[]>;
  /** Whether the platform gives us resource timings to read at all. */
  supported: boolean;
}

/**
 * The attempt stream for the window, recomputed once a second.
 *
 * Same subscription and same ring as `useLinkStats` — one observer, one
 * buffer, two readings off it, so the meter's median and this band can never
 * describe different traffic.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAttemptWindow(): AttemptWindow {
  const now = useNow(true);
  const serial = useSyncExternalStore(subscribeLink, getLinkSerial, getLinkSerial);
  return useMemo(() => {
    /* Clamped to the session's own start. A console that has been open for
       twenty seconds has not "made no requests for the first hundred" — it
       did not exist — and painting 87% of the lane empty would be the strip
       inventing an outage out of the page load. The ruler is labelled off the
       span, so it says `−20s` on a young session and `−2 min` on a settled
       one. */
    const origin = typeof performance === 'undefined' ? 0 : performance.timeOrigin;
    const from = Math.max(now - LINK_WINDOW_MS, origin);
    const bySource = new Map<string, ReadAttempt[]>();
    for (const s of linkRing) {
      if (s.t < from || s.src === null) continue;
      let bucket = bySource.get(s.src);
      if (bucket === undefined) {
        bucket = [];
        bySource.set(s.src, bucket);
      }
      bucket.push({ t: s.t, ok: s.ms !== null });
    }
    return { from, to: now, bySource, supported: linkSupported };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serial, now]);
}

/**
 * Four bars, and the grade ladder they are drawn against is printed in the
 * popover. §13 tell 28: a healthy link is ONE hue — here it is no hue at all,
 * greyscale like the rest of the chrome — and only a breaching one takes a
 * state colour. A console whose link meter is coloured while everything is
 * fine has spent the alarm channel on decoration.
 *
 * Speed is not the only way a link is bad. Reads that never landed are
 * excluded from the median (a 0.7ms dead socket would otherwise read as the
 * fastest link the console has ever had), so loss has to be graded
 * separately or a comprehensively failing endpoint would draw four bars off
 * the handful of reads that still work. Loss caps the grade; total loss
 * empties the meter.
 */
function gradeLink(p50: number | null, ok = 0, failed = 0): { bars: number; tone: Tone } {
  const total = ok + failed;
  if (p50 === null) return { bars: 0, tone: total > 0 ? 'error' : 'neutral' };

  let bars: number;
  let tone: Tone;
  if (p50 <= LINK_LADDER[0]) {
    bars = 4;
    tone = 'nominal';
  } else if (p50 <= LINK_LADDER[1]) {
    bars = 3;
    tone = 'nominal';
  } else if (p50 <= LINK_LADDER[2]) {
    bars = 2;
    tone = 'warn';
  } else {
    bars = 1;
    tone = 'error';
  }

  const loss = total === 0 ? 0 : failed / total;
  if (loss >= 0.5) return { bars: 1, tone: 'error' };
  if (loss > 0) return { bars: Math.min(bars, 2), tone: tone === 'error' ? 'error' : 'warn' };
  return { bars, tone };
}

/** Three glyphs and a unit, at every magnitude: `8`, `137`, `1.4s`, `12s`. */
function msShort(ms: number | null): { value: string; unit: string } {
  if (ms === null) return { value: NO_VALUE, unit: '' };
  if (ms < 1_000) return { value: String(Math.round(ms)), unit: 'ms' };
  const s = ms / 1_000;
  return { value: s < 10 ? s.toFixed(1) : String(Math.round(s)), unit: 's' };
}

/** What the link actually carried. KiB below a megabyte, MiB above. */
function bytesShort(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KiB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MiB`;
}

/** The meter itself. Four bars, no labels — the figure beside it is the label. */
function LinkMeter({ bars, tone }: { bars: number; tone: Tone }) {
  return (
    <span className={styles.meter} data-tone={tone} aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <span key={i} className={styles.meterBar} data-on={i < bars} />
      ))}
    </span>
  );
}

/* ==========================================================================
   THE FAILURE LOG — what a dead read actually says.

   A console that reports a broken source as a 6px red dot has told the
   operator that something is wrong and nothing else: not what, not since
   when, not whether anything it is currently drawing came from before the
   break. Four facts turn that dot into a diagnosis, and all four are already
   in the query cache:

     WHICH ENDPOINT   `/api/robots`, the string a person can curl.
     WHY              the error verbatim — `Failed to fetch` (the browser's own
                      words for a dead socket) or `HTTP 503`. Never a
                      paraphrase: "Something went wrong" is not a fault report.
     HOW BAD          consecutive failed reads since the last good one. One is
                      a blip on a poll that will self-heal in five seconds;
                      forty is an outage.
     LAST GOOD READ   as an ABSOLUTE Zulu stamp, not a relative age. §11.3 is
                      explicit about this and it is the one place in the
                      console where relative time is wrong: when reads stop,
                      the relative age is the only thing still moving on the
                      screen and a moving number reads as a live one.

   TanStack tracks none of "consecutive" for us — `fetchFailureCount` resets at
   the top of every fetch (query-core: `fetchState()`), so under a 5s poll it
   never rises above the retry count, and `errorUpdateCount` is a lifetime
   total that never resets on success. So the log below counts the runs itself,
   off the cache's own event stream: one subscription per QueryClient, keyed by
   query hash, incremented on an error landing and zeroed on data landing.
   Event-driven rather than sampled in render, so a StrictMode double-render
   cannot double-count.
   ========================================================================== */

interface FailNote {
  /** Consecutive failed reads since the last successful one. 0 = healthy. */
  runs: number;
  /** The last error, verbatim. */
  reason: string;
  /** Epoch ms of the last successful read. 0 = never, this session. */
  lastGood: number;
  /** Epoch ms of the most recent failed attempt. */
  lastFail: number;
}

const FAIL_LOG = new WeakMap<QueryClient, Map<string, FailNote>>();

/** The error's own words, normalised only where they are pure noise. */
function reasonOf(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err ?? '');
  /* The hooks throw `status 503` for a non-2xx; spell it the way an operator
     reads a status line. Everything else — `Failed to fetch`, `NetworkError
     when attempting to fetch resource`, a parse error — is passed through
     untouched, because the browser's phrasing is the searchable one. */
  const http = /^status (\d{3})$/.exec(msg);
  if (http) return `HTTP ${http[1]}`;
  return msg === '' ? 'unknown error' : msg;
}

function failLog(qc: QueryClient): Map<string, FailNote> {
  const existing = FAIL_LOG.get(qc);
  if (existing) return existing;

  const log = new Map<string, FailNote>();
  FAIL_LOG.set(qc, log);

  const seen = new Map<string, { d: number; e: number }>();
  const observe = (q: {
    queryHash: string;
    state: {
      dataUpdateCount: number;
      errorUpdateCount: number;
      dataUpdatedAt: number;
      errorUpdatedAt: number;
      error: unknown;
    };
  }) => {
    const prev = seen.get(q.queryHash) ?? { d: 0, e: 0 };
    const d = q.state.dataUpdateCount;
    const e = q.state.errorUpdateCount;
    if (d === prev.d && e === prev.e) return;
    seen.set(q.queryHash, { d, e });
    const note: FailNote = log.get(q.queryHash) ?? {
      runs: 0,
      reason: '',
      lastGood: 0,
      lastFail: 0,
    };
    /* Order matters: a cycle that both errored and succeeded (a refetch racing
       a poll) ends healthy, so the success is applied last. */
    if (e > prev.e) {
      note.runs += e - prev.e;
      note.reason = reasonOf(q.state.error);
      note.lastFail = q.state.errorUpdatedAt;
    }
    if (d > prev.d) {
      note.runs = 0;
      note.lastGood = q.state.dataUpdatedAt;
    }
    log.set(q.queryHash, note);
  };

  qc.getQueryCache().subscribe((ev) => {
    if (ev.type === 'removed') {
      log.delete(ev.query.queryHash);
      seen.delete(ev.query.queryHash);
      return;
    }
    observe(ev.query);
  });

  /* Whatever is already in the cache when the log is installed. Seeded from
     the STAMPS rather than replayed through `observe`: the counts alone cannot
     say which of a query's five successes and three failures happened last,
     and replaying them in count order would report a currently-failing source
     as healthy. `errorUpdateCount` is the whole lifetime here, which is the
     best available floor for a run whose start we did not witness. */
  for (const q of qc.getQueryCache().getAll()) {
    const st = q.state;
    seen.set(q.queryHash, { d: st.dataUpdateCount, e: st.errorUpdateCount });
    log.set(q.queryHash, {
      runs: isFailing(st) ? Math.max(st.errorUpdateCount, 1) : 0,
      reason: isFailing(st) ? reasonOf(st.error ?? st.fetchFailureReason) : '',
      lastGood: st.dataUpdatedAt,
      lastFail: st.errorUpdatedAt,
    });
  }
  return log;
}

/** The worst run of failures across one source family's mounted queries. */
function failureOf(qc: QueryClient, prefix: readonly unknown[]): FailNote | null {
  const log = failLog(qc);
  let worst: FailNote | null = null;
  for (const q of qc.getQueryCache().findAll({ queryKey: prefix as unknown[] })) {
    if (q.getObserversCount() === 0) continue;
    const note = log.get(q.queryHash);
    if (!note || note.runs === 0) continue;
    if (worst === null || note.runs > worst.runs) worst = note;
  }
  return worst;
}

/* The source the transport cluster falls back to when it has to name one and
   nothing is wrong: the fleet list, which is the console's spine and the read
   every other surface hangs off. The bar does not PRINT its age — freshness
   belongs to the surface strip one band below, in the scope the operator is
   looking at — but the worst-source picker needs an incumbent to hold, so
   that a healthy console and a console with two equally-late channels do not
   swap labels on every poll. */
const BAR_SOURCE = 'robots';

interface Read {
  /** Newest successful read across the family. 0 = never. */
  updatedAt: number;
  fetching: boolean;
  errored: boolean;
  /** At least one query in this family is actually mounted. */
  mounted: boolean;
}

/**
 * `status === 'error'` is NOT the question, and reading it as one is how the
 * last build flickered a dead platform on and off twice a poll.
 *
 * query-core's `fetchState()` clears `error` and sets `status: 'pending'` at
 * the top of every fetch **when the query has no data** — so a source that has
 * never answered spends most of a 5s poll cycle reporting no error at all. The
 * bar read POLL, the strip dropped its fault row, and the workspace's zeroed
 * status bars came back, on a two-second cycle, against an API that was
 * comprehensively dead.
 *
 * The honest question is which terminal event happened LAST. `errorUpdatedAt`
 * and `dataUpdatedAt` both survive a fetch reset, so their order is stable
 * across the whole retry cycle and flips exactly once, on the read that
 * actually succeeds.
 */
function isFailing(state: {
  status: string;
  dataUpdatedAt: number;
  errorUpdatedAt: number;
}): boolean {
  if (state.status === 'error') return true;
  return state.errorUpdatedAt > 0 && state.errorUpdatedAt > state.dataUpdatedAt;
}

function readOf(qc: QueryClient, prefix: readonly unknown[]): Read {
  const out: Read = { updatedAt: 0, fetching: false, errored: false, mounted: false };
  for (const q of qc.getQueryCache().findAll({ queryKey: prefix as unknown[] })) {
    if (q.getObserversCount() === 0) continue;
    out.mounted = true;
    if (q.state.dataUpdatedAt > out.updatedAt) out.updatedAt = q.state.dataUpdatedAt;
    if (q.state.fetchStatus === 'fetching') out.fetching = true;
    if (isFailing(q.state)) out.errored = true;
  }
  return out;
}

/**
 * Re-render on every query-cache event.
 *
 * The transport needs the shape of a whole SET of queries — which are mounted,
 * when each last landed, which failed — not one query's data, so it reads the
 * cache directly. Deliberately scoped to this leaf: subscribing the whole top
 * bar would re-render the fleet rollup and the palette on every poll.
 */
function useCacheTick(qc: QueryClient): number {
  const [tick, bump] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    /* Coalesced and deferred out of the render phase. A workspace mounting its
       queries emits cache events WHILE it renders, and bumping synchronously
       there is a setState-during-render — React logs it, and in a console that
       polls five queries it would log it on every navigation. One timeout per
       burst also collapses "28 robots landed" into a single re-render. */
    let queued = 0;
    const unsubscribe = qc.getQueryCache().subscribe(() => {
      if (queued !== 0) return;
      queued = window.setTimeout(() => {
        queued = 0;
        bump();
      }, 0);
    });
    return () => {
      if (queued !== 0) window.clearTimeout(queued);
      unsubscribe();
    };
  }, [qc]);
  return tick;
}

function everyLabel(ms: number): string {
  return ms === 0 ? 'on demand' : `${ms / 1_000}s`;
}

interface SourceRead extends Read {
  spec: SourceSpec;
  ageMs: number | null;
  tone: Tone;
  /** Nothing has arrived for three cadences. */
  late: boolean;
}

function evaluate(spec: SourceSpec, r: Read, now: number, visible: boolean): SourceRead {
  const ageMs = r.updatedAt > 0 ? Math.max(0, now - r.updatedAt) : null;
  /* Three cadences of slack before a poll reads as late. Below that a 5s query
     is "late" for a fifth of every cycle and the number is noise. A source
     that does not poll at all is never late — it is a document. A tab nobody
     is looking at is not late either: its timers are legitimately stopped, and
     lighting the whole cluster amber on return would be an alarm about the
     browser rather than about the fleet. A HELD console, by contrast, does go
     late: the operator stopped the clock and is entitled to watch it drift. */
  const late =
    spec.everyMs > 0 && visible && ageMs !== null && ageMs > spec.everyMs * 3;
  const tone: Tone = r.errored ? 'error' : !r.mounted ? 'neutral' : late ? 'warn' : 'nominal';
  return { ...r, spec, ageMs, tone, late };
}

/* --------------------------------------------------------------------------
   The freshness of ONE SURFACE, computed by the same three functions the
   transport uses. The surface strip's right zone is the only caller.

   The age reported is the STALEST of the surface's sources, not the newest:
   a signal explorer drawn from a 1-second-old telemetry read and a 40-second-
   old fleet read is a 40-second-old picture, and quoting the 1 would be the
   console choosing the flattering number.
   -------------------------------------------------------------------------- */

/**
 * A source this surface is drawn from that has stopped answering — the whole
 * diagnosis, ready to print. Null while every source is healthy.
 */
export interface SurfaceFault {
  /** The endpoint that is not answering. */
  path: string;
  /** How the console names that source. */
  label: string;
  /** The error, verbatim: `Failed to fetch`, `HTTP 503`. */
  reason: string;
  /** Consecutive failed reads since the last successful one. */
  runs: number;
  /**
   * How many OTHER sources behind this surface are also failing. The strip
   * names one endpoint — the one to fix first — and a row that named it
   * without saying "and one more" would let an operator close an outage on the
   * strength of a single green curl.
   */
  others: number;
  /** Absolute Zulu of the last good read. Null = never, this session. */
  lastGood: string | null;
  /** Absolute Zulu of the most recent failed attempt. */
  lastFail: string | null;
  /**
   * NOTHING this surface is drawn from has ever landed, so every figure the
   * workspace could paint would be invented. The shell refuses to let a
   * canvas render in that state — see AppShell's `NoData`.
   */
  unread: boolean;
}

/**
 * One source this surface is drawn from, as the shell needs to print it when
 * the surface has nothing to draw. Everything an operator triaging an outage
 * asks first: which endpoint, how often it is meant to answer, whether it
 * answered, why not, and when it last did.
 */
export interface SurfaceSource {
  id: string;
  label: string;
  path: string;
  /** Declared cadence in ms. 0 = read on demand. */
  everyMs: number;
  /** Age of this source's own last good read. Null = never, this session. */
  ageMs: number | null;
  /** Absolute Zulu of that read. Null = never, this session. */
  lastReadIso: string | null;
  errored: boolean;
  /** The error verbatim, when this source is the one that is broken. */
  reason: string | null;
  /** Consecutive failed reads since its last good one. */
  runs: number;
}

export interface SurfaceRead {
  /** Stalest age across the surface's mounted sources. null = never read. */
  ageMs: number | null;
  /**
   * The ABSOLUTE Zulu stamp behind `ageMs` — the moment the stalest source
   * last answered. The strip prints the relative age because that is what a
   * glance wants; the status bar prints this, because §11.3 is explicit that
   * when reads stop the relative age is the only number still moving on the
   * screen and a moving number reads as a live one. Null = never read.
   */
  lastReadIso: string | null;
  /**
   * The surface's SLOWEST declared poll interval — how often the picture can
   * change at all. Null where nothing on the surface polls (the catalog is a
   * document, not a feed).
   */
  everyMs: number | null;
  tone: Tone;
  /**
   * Sources are arriving on a timer and the console is not held — AND at least
   * one of them has actually answered.
   *
   * A ZERO IS A MEASUREMENT AND AN UNREAD SOURCE IS NOT, and this flag is the
   * one the strip turns into the word `Polling` with a filled dot. It used to
   * be `polled.length > 0`: mounting a query was enough to claim the surface
   * was current, so a cold start against a hung API flew `● Polling` over
   * `0 registered` and an empty table for as long as the request hung. The
   * evidence a liveness claim needs is a read that landed.
   */
  live: boolean;
  /**
   * NOT ONE mounted source has ever answered this session, so there is no
   * picture at all — not even an old one. Distinct from `tone === 'error'`:
   * this is also true while a first read is simply still in flight, and the
   * strip must not say `Polling` in either case.
   */
  unread: boolean;
  /** Nothing on this surface polls — it is a document, not a feed. */
  onDemand: boolean;
  /** A read is in flight right now. */
  fetching: boolean;
  /** One line naming every source, its interval and its own age. */
  detail: string;
  /** The failing source, if any — endpoint, reason, run length, last good. */
  fault: SurfaceFault | null;
  /**
   * Every source this surface is mounted against, in declared order. The strip
   * never prints this — one fault line is the right density for a band 36px
   * tall — but the canvas does, when there is nothing else to draw: see
   * AppShell's `NoData`. Same three functions behind it as everything else on
   * this bar, so the ledger and the fault line cannot disagree.
   */
  sources: readonly SurfaceSource[];
  /** Re-read this surface's sources, held or not. */
  reread: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSurfaceRead(ids: readonly string[]): SurfaceRead {
  const qc = useQueryClient();
  const tick = useCacheTick(qc);
  const visible = useDocumentVisible();
  const { held } = useLiveHold();
  const now = useNow(true);
  const key = ids.join(',');

  const reread = useCallback(() => {
    for (const id of key.split(',')) {
      const spec = SOURCE_BY_ID.get(id);
      if (spec) void qc.refetchQueries({ queryKey: spec.prefix as unknown[] });
    }
  }, [qc, key]);

  return useMemo(() => {
    /* Installed eagerly, on the first render of the first surface, so the log
       is watching before anything can break rather than from whenever the
       first failure happened to be rendered. */
    failLog(qc);
    const specs = key
      .split(',')
      .map((id) => SOURCE_BY_ID.get(id))
      .filter((s): s is SourceSpec => s !== undefined);
    const reads = specs
      .map((s) => evaluate(s, readOf(qc, s.prefix), now, visible))
      .filter((s) => s.mounted);

    const polled = reads.filter((s) => s.spec.everyMs > 0);
    const onDemand = reads.length > 0 && polled.length === 0;
    /* The STALEST source is the surface's age, so the stamp behind it is the
       OLDEST successful read — one number and one stamp, derived from the
       same value so the bottom band and the strip can never quote two
       different moments for one picture. */
    const stamps = reads.map((s) => s.updatedAt).filter((t) => t > 0);
    const oldest = stamps.length === 0 ? 0 : Math.min(...stamps);
    const ageMs = oldest === 0 ? null : Math.max(0, now - oldest);
    const lastReadIso = oldest === 0 ? null : new Date(oldest).toISOString();
    const tone: Tone = reads.some((s) => s.tone === 'error')
      ? 'error'
      : reads.some((s) => s.late)
        ? 'warn'
        : reads.length === 0
          ? 'neutral'
          : 'nominal';

    /* The first errored source in DECLARED order, which is the surface's own
       order of dependence: /fleet declares `robots, stats`, so a fleet that
       cannot be listed is reported before the summary computed from it. One
       fault line, naming the source the operator has to fix first. */
    const broken = reads.find((s) => s.errored);
    const brokenCount = reads.filter((s) => s.errored).length;
    const fault: SurfaceFault | null = broken
      ? (() => {
          const note = failureOf(qc, broken.spec.prefix);
          return {
            path: broken.spec.path,
            label: broken.spec.label,
            reason: note?.reason ?? 'read failed',
            /* At least one: the query is in `error`, so a read has failed even
               if this log was installed after it happened. */
            runs: Math.max(note?.runs ?? 0, 1),
            others: Math.max(brokenCount - 1, 0),
            lastGood:
              broken.updatedAt > 0 ? new Date(broken.updatedAt).toISOString() : null,
            lastFail:
              note && note.lastFail > 0 ? new Date(note.lastFail).toISOString() : null,
            /* Every mounted source is empty — there is no picture at all, not
               even an old one. */
            unread: reads.every((s) => s.updatedAt === 0),
          };
        })()
      : null;

    return {
      ageMs,
      lastReadIso,
      everyMs: polled.length === 0 ? null : Math.max(...polled.map((s) => s.spec.everyMs)),
      tone,
      fault,
      sources: reads.map((s) => {
        const note = failureOf(qc, s.spec.prefix);
        return {
          id: s.spec.id,
          label: s.spec.label,
          path: s.spec.path,
          everyMs: s.spec.everyMs,
          ageMs: s.ageMs,
          lastReadIso: s.updatedAt > 0 ? new Date(s.updatedAt).toISOString() : null,
          errored: s.errored,
          reason: s.errored ? (note?.reason ?? 'read failed') : null,
          runs: s.errored ? Math.max(note?.runs ?? 0, 1) : 0,
        };
      }),
      /* `polled.some(landed)` is the whole fix: a mounted timer is an
         intention, and a liveness word needs a reading. See `live` above. */
      live: !held && polled.some((s) => s.updatedAt > 0),
      unread: reads.length > 0 && reads.every((s) => s.updatedAt === 0),
      onDemand,
      fetching: reads.some((s) => s.fetching),
      detail:
        reads.length === 0
          ? 'no source mounted'
          : reads
              .map(
                (s) =>
                  `${s.spec.label} ${everyLabel(s.spec.everyMs)} · ${
                    s.ageMs === null ? 'never read' : `${duration(s.ageMs)} ago`
                  }`,
              )
              .join('\n'),
      reread,
    };
    // `tick` is the cache subscription; `now` ticks the ages.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qc, key, now, visible, held, tick, reread]);
}

/* --------------------------------------------------------------------------
   EVERY SOURCE THE CONSOLE IS READING, not just the ones behind this surface.

   The strip's fault line is surface-scoped on purpose — it names the endpoint
   the operator has to fix to get THIS screen back. The top bar's alarm is
   console-scoped, because a source the current surface does not read is
   invisible to the strip and still has to be reportable. Those two scopes
   used to meet on the outage canvas and disagree: the bar said `robots +2
   failing` while the ledger under it listed two rows, so one screen carried
   two counts of one outage with nothing on it to say why.

   So the ledger takes the console's whole source list and GROUPS it — what
   this surface reads, then what the rest of the console reads — which is the
   collapsible-group-with-a-count idiom from `skydio-dfr-incident-dispatch.png`
   applied to endpoints. The bar's `+2` is now something the operator can
   count on the screen underneath it.
   -------------------------------------------------------------------------- */
// eslint-disable-next-line react-refresh/only-export-components
export function useConsoleSources(): readonly SurfaceSource[] {
  const qc = useQueryClient();
  const tick = useCacheTick(qc);
  const visible = useDocumentVisible();
  const now = useNow(true);

  return useMemo(() => {
    failLog(qc);
    return SOURCES.map((spec) => evaluate(spec, readOf(qc, spec.prefix), now, visible))
      .filter((s) => s.mounted)
      .map((s) => {
        const note = failureOf(qc, s.spec.prefix);
        return {
          id: s.spec.id,
          label: s.spec.label,
          path: s.spec.path,
          everyMs: s.spec.everyMs,
          ageMs: s.ageMs,
          lastReadIso: s.updatedAt > 0 ? new Date(s.updatedAt).toISOString() : null,
          errored: s.errored,
          reason: s.errored ? (note?.reason ?? 'read failed') : null,
          runs: s.errored ? Math.max(note?.runs ?? 0, 1) : 0,
        };
      });
    // `tick` is the cache subscription; `now` ticks the ages.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qc, now, visible, tick]);
}

/* How the PUSH plane is doing. Internal to the popover's `Push` row — the word
   on the bar is about the console's data supply as a whole, below. */
/* --------------------------------------------------------------------------
   THE CONSOLE'S OWN VITALS — what is still true when the platform is not.

   An operator staring at a dead console asks one question before any other:
   is it me or is it them. Every figure below answers some part of it and NOT
   ONE of them comes from the API — they are the browser's own network verdict,
   the address bar, the build's config, the operator's own hold switch, and the
   `PerformanceResourceTiming` counters this document measured itself.

   That is the whole point: this is the block that has to keep working when
   `/api/*` does not. `rerun-mcap-lidar-dual-3d-timeline.png` names its
   endpoint in the chrome while it waits (`Waiting for data on
   rerun+http://127.0.0.1:9876/proxy…`) and `rerun-live-perf-hud-blueprint-
   tabs.png` names the host it is measuring (`4 ms latency for 100.120.1.84`).
   Same posture, our facts.

   Consumed by the shell's `NoData` — see AppShell.
   -------------------------------------------------------------------------- */
export interface ConsoleProbe {
  /** The browser's own answer, not one inferred from failed reads. */
  online: boolean;
  /** Where the app and the API are being served from. */
  host: string;
  /** Deployment plane, read off the origin. */
  env: string;
  /** Successful and failed requests inside the link window. */
  ok: number;
  failed: number;
  /** Bytes the link actually carried in the window, headers included. */
  bytes: string;
  /** How far back the counters look, in words. */
  window: string;
  /** Why the timers are or are not running. */
  timers: string;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useConsoleProbe(): ConsoleProbe {
  const now = useNow(true);
  const link = useLinkStats(now);
  const visible = useDocumentVisible();
  const { held } = useLiveHold();
  const online = useOnline();
  const env = useMemo(environment, []);
  return {
    online,
    host: typeof window === 'undefined' ? NO_VALUE : window.location.host,
    env: env.label.toLowerCase(),
    ok: link.ok,
    failed: link.failed,
    bytes: bytesShort(link.bytes),
    window: `${LINK_WINDOW_MS / 60_000} min`,
    timers: held
      ? 'held by operator'
      : visible
        ? 'running'
        : 'stopped — window in background',
  };
}

type Mode = 'live' | 'poll' | 'down';

/* --------------------------------------------------------------------------
   THE ONE WORD ON THE BAR is not "how is MQTT" — it is "is anything arriving".

   Four states, four glyphs each, so the word never changes width:

     LIVE   the push link is up; readings arrive when the robot sends them
     POLL   the push link is absent or down and REST polling is carrying it —
            data IS arriving, on a timer. The dot steps to amber when the push
            plane is the reason, and the popover's `Push` row says why.
     HOLD   the operator stopped every timer. Nothing is arriving BY CHOICE,
            and a bar that still said POLL would be describing an intention
            rather than a fact.
     DOWN   reads are failing. Nothing is arriving at all.

   The last round could not say two of these. It said POLL while every read
   404'd (with only a 6px dot to disagree) and POLL while the operator was
   holding the console. Both are the bar claiming a supply it does not have.

   `HOLD`, not `HELD`: `held` is already a word in this console — it is what
   the fleet's own matrix key calls a robot in maintenance, and it labels a
   census row on /fleet and a tone in the status matrix's key. One
   console cannot carry two meanings of one word, and the state word that gets
   to change is the one whose control is right here: the popover's verb is
   `Hold polling`, so the state it produces reads `HOLD`. The robot word
   belongs to `components/state`'s MATRIX_KEY and is shared with the status
   matrix on /fleet, which is why it is not the one that moved.
   -------------------------------------------------------------------------- */
const SUPPLY_WORD = { live: 'LIVE', poll: 'POLL', held: 'HOLD', down: 'DOWN' } as const;
type SupplyState = keyof typeof SUPPLY_WORD;

/* The same four states, spelled out for the floor. The 34px band has room for
   four glyphs; the status bar has room for the sentence, and a corner that
   says `rest polling` needs no legend. */
const SUPPLY_PHRASE: Record<SupplyState, string> = {
  live: 'mqtt push',
  poll: 'rest polling',
  held: 'polling held',
  down: 'no reads landing',
};

function Transport() {
  const { data: auth } = useAuth();
  const tenant = auth?.user?.tenant;
  const cfg = realtimeConfig();
  const configured = cfg !== null;
  const qc = useQueryClient();
  const tick = useCacheTick(qc);
  const visible = useDocumentVisible();
  const { held, setHeld } = useLiveHold();
  const now = useNow(true);
  const link = useLinkStats(now);

  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  /* Link observation only — `keysFor` returns nothing. A tenant subtree at 28
     robots × 5s of telemetry would otherwise invalidate the fleet query about
     six times a second. Freshness stays the polling query's job; this hook is
     here to report whether the push link is actually up. */
  const rt = useRealtimeInvalidation({
    topicFilters: tenant !== undefined && configured ? [`fleet/${tenant}/#`] : [],
    keysFor: () => [],
    enabled: tenant !== undefined && configured,
  });

  const mode: Mode = !configured ? 'poll' : rt.connected ? 'live' : 'down';

  /* Every source the console is currently reading, in the declared order —
     the popover's rows. The bar's one inline slot is picked out of it. */
  const all = useMemo(
    () =>
      SOURCES.map((s) => ({
        ...evaluate(s, readOf(qc, s.prefix), now, visible),
        /* The failure run, so a broken source can print WHY under its row
           rather than just going red in the age column. */
        note: failureOf(qc, s.prefix),
      })).filter((s) => s.mounted),
    // `tick` is the cache subscription; `now` ticks the ages.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc, now, visible, tick],
  );

  const fleet = useMemo(() => {
    const found = all.find((s) => s.spec.id === BAR_SOURCE);
    if (found) return found;
    const spec = SOURCE_BY_ID.get(BAR_SOURCE) ?? SOURCES[0]!;
    return evaluate(
      spec,
      { updatedAt: 0, fetching: false, errored: false, mounted: false },
      now,
      visible,
    );
  }, [all, now, visible]);

  /* WHICH CHANNEL — §13 tell 27.

     "One global connection light standing in for four channels" is the defect,
     and the fix `skydio-remote-flight-deck-live.png` ships is three meters
     side by side: you can see that GPS is at 2/4 while the fused control link
     is at 4/4, from across the room, without hovering. We have six polled REST
     families and 78px, so the bar cannot draw six meters — but it can always
     name the worst one, which answers the same question.

     So the named slot is not "the fleet" any more, it is THE WORST MOUNTED
     SOURCE: errored beats late beats fresh, and within a rank the oldest read
     wins. A healthy console names the fleet, because nothing outranks it and
     it is the console's spine. A console where `/api/stats` is 503ing and
     `/api/robots` is fine names `stats`, in red, in the same 78px — which is
     the exact case the last round rendered as a calm blue `POLL … fleet 3s`.

     Ties go to the incumbent (`>` not `>=`), so a fleet and a stats read that
     are both healthy, or both two cadences late, do not swap the label back
     and forth on every poll. */
  const worst = useMemo(() => {
    const rank = (s: SourceRead) => (s.errored ? 2 : s.late ? 1 : 0);
    let pick = fleet;
    for (const s of all) {
      const r = rank(s);
      const p = rank(pick);
      if (r > p) pick = s;
      else if (r === p && r > 0 && (s.ageMs ?? Infinity) > (pick.ageMs ?? Infinity)) pick = s;
    }
    return pick;
  }, [all, fleet]);

  /* Every mounted source that has stopped answering. The count is what makes
     "one endpoint is down" and "the platform is down" different readings, and
     it is what the word DOWN is gated on below. */
  const brokenSources = useMemo(() => all.filter((s) => s.errored), [all]);

  /* What the alarm slot says, or `null` for "every channel is answering on
     time". `down` outranks `late`; within a rank the source the `worst`
     picker already chose leads, so the slot and the popover's first red row
     name the same endpoint. */
  const channel = useMemo(() => {
    const late = all.filter((s) => !s.errored && s.late);
    if (brokenSources.length > 0) {
      return {
        label: (brokenSources.find((s) => s.spec.id === worst.spec.id) ?? brokenSources[0]!)
          .spec.label,
        more: brokenSources.length - 1,
        word: 'failing',
        tone: 'error' as Tone,
      };
    }
    if (late.length > 0) {
      return {
        label: (late.find((s) => s.spec.id === worst.spec.id) ?? late[0]!).spec.label,
        more: late.length - 1,
        word: 'late',
        tone: 'warn' as Tone,
      };
    }
    return null;
  }, [all, brokenSources, worst]);

  /* The word and the dot are the honest overlay of three facts: how data is
     meant to arrive, whether it has stopped arriving, and whether the operator
     has stopped it on purpose. A console that shows a calm blue POLL while a
     read is four minutes old is lying by omission — and one that shows it
     while an operator is holding the screen is lying twice. */
  const supply: SupplyState = fleet.errored
    ? 'down'
    : held
      ? 'held'
      : mode === 'live'
        ? 'live'
        : 'poll';

  /* The WORD is whether anything is arriving; the DOT is what is wrong with
     it. They are deliberately gated on different facts, because "the console
     is still being fed" and "one of its six feeds has stopped" are both true
     during a partial outage and a bar that could only say one of them would
     have to pick — and would pick the flattering one.

     So the dot escalates on ANY errored source, not only on the fleet's. A
     `POLL` pill with a red dot beside a red `stats —` is the honest rendering
     of "/api/robots is fine, /api/stats is dead": nothing about the word is a
     lie, and nothing about the outage is hidden. */
  const dotTone: Tone =
    supply === 'down' || brokenSources.length > 0
      ? 'error'
      : supply === 'held' || worst.tone === 'warn'
        ? 'warn'
        : supply === 'live'
          ? 'live'
          : mode === 'down'
            ? 'warn'
            : 'ok';

  /* Published for the shell's status bar, which spells the same state out in
     the console's bottom-left corner beside the tenant and the host. Written
     from an effect, never from render: this is a store outside React and a
     write during render would be a mutation the reconciler cannot see. */
  useEffect(() => {
    publishSupply({ state: supply, phrase: SUPPLY_PHRASE[supply], tone: dotTone });
  }, [supply, dotTone]);

  const reread = useCallback(() => {
    for (const s of SOURCES) void qc.refetchQueries({ queryKey: s.prefix as unknown[] });
  }, [qc]);

  useEffect(() => {
    if (!open) return;
    function onPointer(e: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    popRef.current?.querySelector<HTMLElement>('button')?.focus();
  }, [open]);

  /* TWO GRADES, ON PURPOSE.

     The METER is the link's overall condition and is capped by loss, because
     a meter is a verdict. The NUMBER beside it is the median round trip and
     takes a colour only when that median itself breaches — an 11ms median
     printed in amber because a different endpoint is 503ing would be the
     console colouring the one figure on the bar that is fine. Reads that
     failed are counted in the popover's own row, in red, where they belong. */
  const grade = gradeLink(link.p50, link.ok, link.failed);
  const speed = gradeLink(link.p50);
  const rtt = msShort(link.p50);

  const summary =
    `Data supply ${SUPPLY_WORD[supply]}${
      supply === 'poll' && mode === 'down' ? ', push link down' : ''
    }. Link ${
      link.p50 === null
        ? 'not measured yet'
        : `${Math.round(link.p50)}ms median over ${link.ok} read${link.ok === 1 ? '' : 's'}`
    }${link.failed > 0 ? `, ${link.failed} failed` : ''}. ${
      brokenSources.length > 0
        ? `${brokenSources.length} source${brokenSources.length === 1 ? '' : 's'} failing: ${
            brokenSources.map((s) => s.spec.path).join(', ')
          }`
        : `Worst source ${worst.spec.label}, ${
            worst.ageMs === null ? 'never read' : `read ${duration(worst.ageMs)} ago`
          }${worst.late ? ', late' : ''}`
    }`;

  return (
    <div className={styles.transportRoot} ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.transport}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${summary}. Open transport detail.`}
        onClick={() => setOpen((v) => !v)}
      >
        {/* WHICH CHANNEL BROKE — §13 tell 27, and an ALARM SLOT, not a readout.

            "One global connection light standing in for four channels" is the
            defect. We read six REST families and cannot draw six meters in a
            34px band, so the bar names the worst one — but only when there IS
            a worst one. A permanently-lit `robots 3s` here was the console
            stating freshness for the second time in 20px: the surface strip
            below owns the read age, in the scope the operator is actually
            looking at, with a re-read verb on it.

            So this slot is dark on a healthy console and lights the instant a
            channel stops answering or falls behind its own cadence — the one
            fact about supply that has no other home, because a source the
            current surface does not read is invisible to the strip. Its word
            comes off `SourceSpec.label`, the same word the popover's table
            and the strip's fault row use, so the three can be correlated;
            `+2` says a single-endpoint fault is not the whole outage. The
            slot is a fixed track either way, so nothing beside it moves. */}
        <span className={styles.alarm} data-on={channel !== null}>
          {channel !== null && (
            <>
              <span className={styles.alarmName} data-tone={channel.tone}>
                {channel.label}
                {channel.more > 0 && <span className={styles.alarmMore}>+{channel.more}</span>}
              </span>
              <span className={styles.alarmWord} data-tone={channel.tone}>
                {channel.word}
              </span>
            </>
          )}
        </span>
        {/* ONE PILL, ONE POSITION, FILLED ONLY WHEN IT IS TRUE.

            §13 tell 22 and `formant-timeline-data-presence-scrubber.png`
            against `formant-fleet-observability-dashboard.png`: the same
            `LIVE` pill in the same place, filled cyan while live and dim slate
            the instant it is not. That is the property — a state you read by
            SHAPE from across the room, not a word you have to focus on — and
            it is why the fill is spent on `LIVE` alone. `POLL`, `HOLD` and
            `DOWN` are the pill switched off, and they carry their state in the
            word's own colour, which is where §3 puts it.

            The dot lives inside the pill because it answers a different
            question from the word: the word is whether anything is arriving,
            the dot is why. `POLL` with an amber dot is push having failed;
            `POLL` with a blue one is push never having been configured. Two
            facts, four glyphs and 6px. */}
        <span className={styles.linkPill} data-mode={supply}>
          <span
            className={styles.linkDot}
            data-tone={dotTone}
            data-hollow={supply !== 'live'}
            data-pulse={supply === 'live'}
            aria-hidden="true"
          />
          <span className={styles.linkWord}>{SUPPLY_WORD[supply]}</span>
        </span>
        {/* The measurement. Four bars against a published ladder and the
            median beside them, because a meter with no number cannot be
            argued with and a number with no meter has to be read rather than
            glanced at. Both shed before the mode word or the age ever do:
            §13 tell 13 wants the distribution, and the distribution is one
            click away in the popover at every width. */}
        <span className={styles.link} data-tone={speed.tone} aria-hidden="true">
          <LinkMeter bars={grade.bars} tone={grade.tone} />
          <span className={styles.linkMs}>
            {rtt.value}
            <Unit unit={rtt.unit} className={styles.linkUnit} />
          </span>
        </span>
      </button>

      {open && (
        <div className={styles.pop} role="dialog" aria-label="Transport detail" ref={popRef}>
          <div className={styles.popHead}>
            <span className={styles.popTitle}>Transport</span>
            <span className={styles.popMode} data-mode={supply}>
              {SUPPLY_WORD[supply]}
            </span>
          </div>

          {/* Why the word up there is the word it is. Every line is read off
              the build's own config — nothing here is written by hand. */}
          <div className={styles.popRows}>
            <PopRow
              label="Push"
              value={
                mode === 'live'
                  ? `mqtt · fleet/${tenant ?? '?'}/#`
                  : configured
                    ? 'mqtt · not connected'
                    : 'mqtt · not configured'
              }
              tone={mode === 'live' ? 'nominal' : mode === 'down' ? 'warn' : 'neutral'}
            />
            <PopRow
              label="Endpoint"
              value={cfg ? cfg.endpoint : 'VITE_IOT_ENDPOINT unset'}
              tone="neutral"
            />
            <PopRow
              label="Fallback"
              value={mode === 'live' ? 'rest polling (idle)' : 'rest polling (active)'}
              tone="neutral"
            />
            <PopRow label="Tenant" value={tenant ?? NO_VALUE} tone="neutral" />
            {/* Three states, not two. "Held" and "backgrounded" both stop the
                timers and they are not the same event: one is a decision the
                operator made and can undo from here, the other is the browser
                and will undo itself. */}
            <PopRow
              label="Timers"
              value={
                held
                  ? 'held by operator'
                  : visible
                    ? 'running'
                    : 'stopped — window in background'
              }
              tone={held || !visible ? 'warn' : 'neutral'}
            />
          </div>

          {/* THE DISTRIBUTION — §13 tell 13. Every figure is measured off
              this document's own resource timings over a named window; none
              of it is asserted. `formant-teleop-link-health-popover.png` is
              the shape (counters, loss, central tendency, spread, volume) and
              the field list is ours, because our transport is polled REST
              rather than a WebRTC session with pings to count. */}
          <div className={styles.popSection}>
            <span className={styles.popEyebrow}>Link</span>
            <span className={styles.popEyebrowNote}>
              request → last byte · {LINK_WINDOW_MS / 60_000} min
            </span>
          </div>
          <div className={styles.popRows}>
            <PopRow
              label="Median"
              value={
                link.p50 !== null
                  ? `${Math.round(link.p50)} ms`
                  : linkSupported
                    ? /* Measurable, nothing measured yet. */ NO_VALUE
                    : 'no resource timing'
              }
              tone={link.p50 === null ? 'neutral' : speed.tone}
            />
            <PopRow
              label="Min / max"
              value={
                link.min === null || link.max === null
                  ? NO_VALUE
                  : `${Math.round(link.min)} / ${Math.round(link.max)} ms`
              }
              tone="neutral"
            />
            <PopRow
              /* Spread, not a second average: a 40ms median with a 400ms
                 standard deviation is a link that is about to be a problem
                 and a bar that printed only the median would call it healthy. */
              label="Jitter (σ)"
              value={link.jitter === null ? NO_VALUE : `${Math.round(link.jitter)} ms`}
              tone="neutral"
            />
            <PopRow
              label="Reads"
              value={`${link.ok} ok · ${link.failed} failed`}
              tone={link.failed > 0 ? 'error' : 'neutral'}
            />
            <PopRow label="Transferred" value={bytesShort(link.bytes)} tone="neutral" />
            {/* The ladder the meter is drawn against, on screen rather than in
                the source — §13 tell 15. */}
            <PopRow
              label="Thresholds"
              value={`≤${LINK_LADDER[0]} · ≤${LINK_LADDER[1]} · ≤${LINK_LADDER[2]} ms`}
              tone="neutral"
            />
          </div>

          {/* One row per source the console is actually reading right now:
              its endpoint, its declared interval, its own measured median, its
              age, and the absolute Zulu stamp of the last successful read.
              The median is per SOURCE and not per console because "the API is
              slow" and "the telemetry read is slow" are different faults —
              §13 tell 27, three meters rather than one light. */}
          <div className={styles.popTable} role="table" aria-label="Sources">
            <div className={styles.popTableHead} role="row">
              <span role="columnheader">Source</span>
              <span role="columnheader">Every</span>
              <span role="columnheader" title="Median round trip over the link window">
                p50
              </span>
              <span role="columnheader">Age</span>
              <span role="columnheader">Last read (UTC)</span>
            </div>
            {all.length === 0 ? (
              <p className={styles.popEmpty}>No source is mounted on this surface.</p>
            ) : (
              all.map((s) => (
                <Fragment key={s.spec.id}>
                  <div className={styles.popTableRow} role="row">
                    <span className={styles.popSrc} role="cell" title={s.spec.path}>
                      {s.spec.label}
                    </span>
                    <span className={styles.popEvery} role="cell">
                      {everyLabel(s.spec.everyMs)}
                    </span>
                    <span
                      className={styles.popRtt}
                      role="cell"
                      data-tone={gradeLink(link.bySource.get(s.spec.id)?.p50 ?? null).tone}
                      title={
                        link.bySource.has(s.spec.id)
                          ? `${link.bySource.get(s.spec.id)!.ok} ok, ${
                              link.bySource.get(s.spec.id)!.failed
                            } failed in the link window`
                          : 'no read measured in the window'
                      }
                    >
                      {(() => {
                        const m = link.bySource.get(s.spec.id);
                        if (m === undefined) return NO_VALUE;
                        const v = msShort(m.p50);
                        return (
                          <>
                            {v.value}
                            <Unit unit={v.unit} className={styles.linkUnit} />
                          </>
                        );
                      })()}
                    </span>
                    <span className={styles.popAge} data-tone={s.tone} role="cell">
                      {duration(s.ageMs, { compact: 1 })}
                    </span>
                    <span className={styles.popStamp} role="cell">
                      {s.updatedAt > 0 ? utc(new Date(s.updatedAt).toISOString()) : NO_VALUE}
                    </span>
                  </div>
                  {/* A broken source says so in its own words, under its own
                      row — the endpoint, the error verbatim, and how many
                      reads in a row have gone that way. */}
                  {s.errored && (
                    <p className={styles.popFail} role="row">
                      <span role="cell">
                        {s.spec.path} — {s.note?.reason ?? 'read failed'} ·{' '}
                        {Math.max(s.note?.runs ?? 0, 1)} in a row
                      </span>
                    </p>
                  )}
                </Fragment>
              ))
            )}
          </div>

          <div className={styles.popFoot}>
            <button type="button" className={styles.popVerb} onClick={reread}>
              <Icon name="refresh" size={12} />
              Re-read all sources
            </button>
            {/* The same switch the surface strip carries, in the place an
                operator goes when they want to know why nothing is moving. */}
            <button
              type="button"
              className={styles.popVerb}
              aria-pressed={held}
              data-on={held}
              onClick={() => setHeld(!held)}
            >
              <Icon name={held ? 'play' : 'pause'} size={12} />
              {held ? 'Resume polling' : 'Hold polling'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PopRow({ label, value, tone }: { label: string; value: string; tone: Tone }) {
  return (
    <div className={styles.popRow}>
      <span className={styles.popLabel}>{label}</span>
      <span className={styles.popValue} data-tone={tone}>
        {value}
      </span>
    </div>
  );
}

/* --------------------------------------------------------------------------
   The clock. Zulu is the console's time and gets the full stamp; the
   operator's own wall clock rides behind it as a dimmer suffix, named, so
   nobody has to work out which one they are reading.
   -------------------------------------------------------------------------- */

function Clock() {
  const now = useNow(true);
  const d = new Date(now);
  const iso = d.toISOString();
  const stamp = utc(iso);
  /* One string, two contrast steps: the date is context and the time is the
     reading, so the seconds are the brightest thing in the cluster and the
     date sits behind them. Split on the space `utc()` puts between them. */
  const cut = stamp.indexOf(' ');
  const date = cut < 0 ? '' : stamp.slice(0, cut);
  const time = cut < 0 ? stamp : stamp.slice(cut + 1);
  const wall = d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  /* THE DAY THE WALL CLOCK IS ON.

     The cluster prints one date and two times, and for eight hours a day in
     the Americas they are not on the same day: `2026-08-28 05:59:17Z` beside
     `00:59 CDT` is Zulu the 28th and local the 27th, and an operator reading a
     session stamp off this bar would file it under the wrong date. Every other
     figure in this console is Zulu for exactly that reason; the wall clock is
     here as a convenience and a convenience may not be ambiguous.

     So the local time carries a day offset whenever it differs from the Zulu
     date — `−1d` behind, `+1d` ahead — and carries nothing at all the rest of
     the time, which is most of the world and most of the day. Computed from
     the two rendered dates rather than from the UTC offset, so a half-hour
     zone and a DST boundary both fall out of it correctly. */
  const wallDay = d.toLocaleDateString('en-CA');
  const zuluDay = iso.slice(0, 10);
  const dayShift = wallDay === zuluDay ? '' : wallDay < zuluDay ? '−1d' : '+1d';
  return (
    <span className={styles.clock} title={`${utc(iso)}  ·  ${local(iso)}`}>
      <time className={styles.utc} dateTime={iso}>
        {date && <span className={styles.utcDate}>{date}</span>}
        <span className={styles.utcTime}>{time}</span>
      </time>
      <span className={styles.wall}>
        {wall}
        {TZ_LABEL ? <span className={styles.tz}>{TZ_LABEL}</span> : null}
        {dayShift !== '' && (
          <span className={styles.tzDay} title={`Local date is ${wallDay}; Zulu date is ${zuluDay}`}>
            {dayShift}
          </span>
        )}
      </span>
    </span>
  );
}

/* --------------------------------------------------------------------------
   Command palette.

   Opens on ⌘K / Ctrl-K, and with an empty query it is not a blank box: it is
   the attention list. The fastest thing an operator can want at any moment is
   the robot that is currently wrong, so that is what is under the cursor
   before they have typed anything.
   -------------------------------------------------------------------------- */

interface PaletteItem {
  key: string;
  group: string;
  /** How many items the group actually has, when the list is truncated. */
  groupTotal: number;
  label: string;
  meta: string;
  detail: string;
  /** A robot's detail is machine readings; a destination's is prose. */
  detailMono: boolean;
  /** Hover readout: why the dot is the colour it is. */
  hint: string;
  /** Robots carry their worst reading. A destination carries no state. */
  tone: Tone | null;
  hollow: boolean;
  path: string;
}

function siteOf(r: Robot): string {
  const s = r.meta['site'];
  return typeof s === 'string' ? s : '';
}

function destinations(): PaletteItem[] {
  const mk = (label: string, path: string, detail: string): PaletteItem => ({
    key: `go:${path}`,
    group: 'Go to',
    groupTotal: 6,
    label,
    meta: path,
    detail,
    detailMono: false,
    hint: '',
    // A navigation row has no state, so it gets no dot. A grey dot that means
    // nothing is decoration (§8, tell #8); the column stays, so the two kinds
    // of row still align.
    tone: null,
    hollow: false,
    path,
  });
  /* Same order the surface strip lists them in, so the console enumerates
     its surfaces once and in one sequence however you arrive at them. */
  return [
    mk('Fleet operations', buildPath('fleet'), 'status matrix · exceptions'),
    mk('Robot list', buildPath('fleet', 'list'), 'every robot, sortable'),
    mk('Fleet map', buildPath('fleet', 'map'), 'geographic'),
    mk('Telemetry', buildPath('telemetry'), 'signal explorer'),
    mk('Commands', buildPath('commands'), 'queue and history'),
    mk('Catalog', buildPath('catalog'), 'morphologies · revisions'),
  ];
}

function robotItem(
  r: Robot,
  group: string,
  groupTotal: number,
  detail: string,
  e: FleetException | undefined,
): PaletteItem {
  /* The same severity `fleetExceptions` gives the status matrix, so a robot is
     the same colour in the palette and in the exception table. */
  const tone = e ? (TONE_BY_SEVERITY[e.severity] ?? 'warn') : 'nominal';
  return {
    key: `robot:${r.robot_id}`,
    group,
    groupTotal,
    label: r.name ?? r.robot_id,
    meta: r.robot_id,
    detail,
    detailMono: true,
    hint: e ? `Out of limits — ${reasonLine(e)}` : 'Every reading inside its limits',
    tone,
    hollow: tone === 'offline',
    path: buildPath('fleet', 'detail', r.robot_id),
  };
}

function matchScore(r: Robot, q: string): number {
  const id = r.robot_id.toLowerCase();
  if (id.startsWith(q)) return 0;
  const name = (r.name ?? '').toLowerCase();
  if (name.startsWith(q)) return 1;
  if (id.includes(q) || name.includes(q)) return 2;
  const rest = `${r.model} ${r.morphology} ${siteOf(r)} ${r.firmware ?? ''}`.toLowerCase();
  return rest.includes(q) ? 3 : -1;
}

function CommandPalette({
  open,
  onClose,
  onNavigate,
}: {
  open: boolean;
  onClose: () => void;
  onNavigate: (path: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();
  const { data, dataUpdatedAt, errorUpdatedAt } = useRobots();
  const now = useNow(open);
  /* Same latch as `isFailing` above, for the same reason: `isError` drops out
     for the length of every retry on a query that has never had data. */
  const failing = errorUpdatedAt > 0 && errorUpdatedAt > dataUpdatedAt;
  const fault = failing ? failureOf(qc, ['robots']) : null;
  const robots = useMemo(() => data ?? [], [data]);
  const exceptions = useMemo(() => fleetExceptions(robots), [robots]);

  const items = useMemo<PaletteItem[]>(() => {
    const q = query.trim().toLowerCase();
    const byId = new Map<string, FleetException>();
    for (const e of exceptions) byId.set(e.robot.robot_id, e);

    const dest = destinations().filter(
      (d) => q === '' || d.label.toLowerCase().includes(q) || d.meta.includes(q),
    );
    if (q === '') {
      // Empty query is not a blank box: it is the attention list. The fastest
      // thing an operator can want is the robot that is currently wrong.
      /* `Out of limits`, the console's one noun for this set — the same words
         the bar's second reading, the Operations tab's title and the nav
         rail's carry, over the same `fleetExceptions`. A palette that named
         the same 13 robots something else would be a fourth spelling. */
      const attn = exceptions
        .slice(0, 7)
        .map((e) => robotItem(e.robot, 'Out of limits', exceptions.length, reasonLine(e), e));
      return [...attn, ...dest];
    }
    const matched = robots
      .map((r) => ({ r, s: matchScore(r, q) }))
      .filter((x) => x.s >= 0)
      .sort((a, b) => a.s - b.s || a.r.robot_id.localeCompare(b.r.robot_id));
    const hits = matched.slice(0, 8).map(({ r }) =>
      robotItem(
        r,
        'Robots',
        matched.length,
        `${LIVENESS_STATE[r.liveness].label} · ${STATUS_STATE[r.status].label}${
          siteOf(r) ? ` · ${siteOf(r)}` : ''
        }`,
        byId.get(r.robot_id),
      ),
    );
    return [...hits, ...dest];
  }, [query, robots, exceptions]);

  useEffect(() => {
    setCursor(0);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setCursor(0);
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>('[data-cursor="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [cursor, open, items]);

  if (!open) return null;

  const run = (item: PaletteItem | undefined) => {
    if (!item) return;
    onNavigate(item.path);
    onClose();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => (items.length === 0 ? 0 : (c + 1) % items.length));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => (items.length === 0 ? 0 : (c - 1 + items.length) % items.length));
      return;
    }
    if (e.key === 'Home') {
      e.preventDefault();
      setCursor(0);
      return;
    }
    if (e.key === 'End') {
      e.preventDefault();
      setCursor(Math.max(0, items.length - 1));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      run(items[cursor]);
    }
  };

  /* Group headers carry their own count and are emitted inline, so the list is
     one flat listbox for the keyboard and a grouped one for the eye. */
  const groups: { name: string; count: number; total: number }[] = [];
  for (const it of items) {
    const last = groups[groups.length - 1];
    if (last && last.name === it.group) last.count += 1;
    else groups.push({ name: it.group, count: 1, total: it.groupTotal });
  }
  let emitted = 0;

  return (
    <div className={styles.paletteScrim} onPointerDown={onClose} role="presentation">
      <div
        className={styles.palette}
        role="dialog"
        aria-modal="true"
        aria-label="Search the fleet"
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className={styles.paletteInput}>
          <Icon name="search" size={14} />
          <input
            ref={inputRef}
            className={styles.paletteField}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Robot, site, morphology, firmware…"
            spellCheck={false}
            autoComplete="off"
            aria-label="Search the fleet"
            aria-controls="palette-list"
            aria-activedescendant={items[cursor] ? `palette-${items[cursor].key}` : undefined}
          />
          <kbd className={styles.kbd}>esc</kbd>
        </div>

        <div className={styles.paletteList} id="palette-list" role="listbox" ref={listRef}>
          {items.length === 0 ? (
            <p className={styles.paletteEmpty}>No robot or view matches “{query.trim()}”.</p>
          ) : (
            groups.map((g) => {
              const slice = items.slice(emitted, emitted + g.count);
              emitted += g.count;
              const base = emitted - g.count;
              return (
                <div key={`${g.name}-${base}`} className={styles.paletteGroup}>
                  <div className={styles.groupHead}>
                    <span>{g.name}</span>
                    {/* A count that hides a truncation is a count that lies. */}
                    <span className={styles.groupCount}>
                      {g.total > g.count ? `${g.count} / ${g.total}` : g.count}
                    </span>
                  </div>
                  {slice.map((it, i) => (
                    <div
                      key={it.key}
                      id={`palette-${it.key}`}
                      role="option"
                      aria-selected={base + i === cursor}
                      data-cursor={base + i === cursor}
                      className={styles.paletteRow}
                      title={it.hint}
                      onPointerMove={() => setCursor(base + i)}
                      onClick={() => run(it)}
                    >
                      {it.tone === null ? (
                        <span aria-hidden="true" />
                      ) : (
                        <span
                          className={styles.rowDot}
                          data-tone={it.tone}
                          data-hollow={it.hollow}
                          aria-hidden="true"
                        />
                      )}
                      <span className={styles.rowLabel}>{it.label}</span>
                      <span className={styles.rowMeta}>{it.meta}</span>
                      <span className={styles.rowDetail} data-mono={it.detailMono}>
                        {it.detail}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </div>

        <div className={styles.paletteFoot}>
          <span>
            <kbd className={styles.kbd}>↑</kbd>
            <kbd className={styles.kbd}>↓</kbd> move
            <kbd className={styles.kbd}>⏎</kbd> open
          </span>
          {/* The list's own provenance. A palette that has no robots because
              the fleet read failed must not read the same as one that has no
              robots because the fleet is empty — this line is the difference,
              and it is the only place in the palette a failure can be told. */}
          <span className={styles.footRead} data-tone={failing ? 'error' : undefined}>
            {failing ? (
              <>
                {NO_VALUE} robots · {fault?.reason ?? 'read failed'}
                {dataUpdatedAt > 0
                  ? ` · last good ${duration(now - dataUpdatedAt)} ago`
                  : ' · never read'}
              </>
            ) : (
              <>
                {robots.length} robots · read{' '}
                {dataUpdatedAt > 0 ? `${duration(now - dataUpdatedAt)} ago` : 'pending'}
              </>
            )}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ========================================================================== */

export function TopBar() {
  const { setPath } = useBrowserRoute();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const env = useMemo(environment, []);

  /* ⌘K, and deliberately ONLY ⌘K.

     `/` is §6's "focus filter" and it belongs to the surface's own filter box
     — /fleet/list already binds it to the field in its toolbar. A global `/`
     that opened this palette would fire alongside it: the operator would get a
     modal over the field they asked to type in. The console's convention is
     the reference set's: a chord for the global palette, the bare key for the
     filter in front of you. */
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const navigate = useCallback(
    (path: string) => {
      setPath(path);
    },
    [setPath],
  );

  return (
    <>
      {/* ------------------------------------------------------------------
          A FIXED SLOT GRID, not a flex row with two greedy spacers.

          Twelve tracks. Every one of them is either a fixed width or one of
          the two symmetric `1fr` gaps, so the x of every control on this bar
          is a pure function of the VIEWPORT and of nothing else — not of how
          many robots are faulted, not of how long the operator's email is, not
          of whether the queue is at 7 or 17, not of which route is open.

          An earlier round sized the bar's clusters from their content and let
          two `flex: 1` spacers absorb the difference. That made the ⌘K control
          travel 124px (x804 → x680) the moment the fleet recovered and the
          `4 fault` cluster shrank — a control an operator reaches for by
          muscle memory, moved by the good news. Nothing on this bar moves for
          data now; it only ever sheds a word for WIDTH, at a published
          breakpoint, and never sheds the word entirely.
          ------------------------------------------------------------------ */}
      <header
        className={styles.topbar}
        /* See `--tb-chord`: the search slot pays for the operator's own
           modifier spelling, so `Ctrl K` is never an ellipsised label. */
        style={APPLE ? undefined : ({ '--tb-chord': '24px' } as React.CSSProperties)}
      >
        {/* Identity is the mark and the plane, and nothing else. The product
            name that used to sit between them ("Command & Control") named the
            thing the operator is already looking at, in 140px of a 34px band
            — the browser tab says it, and every reference header in the set
            carries a mark and a state, never a tagline. */}
        <div className={styles.brand}>
          <span className={styles.mark}>F3</span>
          <span
            className={styles.env}
            data-env={env.id}
            title={`Deployment: ${env.label} · ${
              typeof window === 'undefined' ? '' : window.location.host
            }`}
          >
            {env.label}
          </span>
        </div>

        <span className={styles.sep} aria-hidden="true" />

        <FleetCounts onOpen={navigate} />

        <span className={styles.sep} aria-hidden="true" />

        <Queue onOpen={navigate} />

        {/* The two flexible tracks. They are the only elastic thing on the bar
            and they are equal, so the search control sits at one x per
            viewport width and reads as the bar's centre zone. */}
        <span className={styles.flex} aria-hidden="true" />

        <button
          type="button"
          className={styles.search}
          onClick={() => setPaletteOpen(true)}
          aria-label={`Search the fleet — shortcut ${APPLE ? 'Command K' : 'Control K'}`}
          aria-keyshortcuts="Meta+K Control+K"
          title={`Search robots, sites, morphologies and builds  ·  ${CHORD}`}
          aria-haspopup="dialog"
        >
          <Icon name="search" size={13} />
          <span className={styles.searchLabel}>Search fleet</span>
          {/* §6: the shortcut is printed IN the affordance, in mono — and in
              the spelling the operator's own keyboard uses. */}
          <kbd className={styles.kbd}>{CHORD}</kbd>
        </button>

        <span className={styles.flex} aria-hidden="true" />

        {/* NO HAIRLINE IN FRONT OF THE TRANSPORT CLUSTER.

            There used to be one, and it sat at the LEFT edge of the 254px
            transport track — 145px in front of the first lit glyph, because
            the leading part of that track is the channel-alarm slot and the
            alarm is dark on a healthy console. A rule that far from the ink it
            delimits reads as a broken control, which is the same defect this
            file already calls out on the account badge's own separator.

            It is not replaced, it is deleted: the bar's elastic gap already
            puts ~250px between the ⌘K control and the mode pill, which is more
            separation than any hairline buys. The rules that remain all hug
            what they delimit. */}
        <Transport />
        <span className={styles.sep} aria-hidden="true" />
        <Clock />
        {/* A slot, not the badge itself: UserBadge renders nothing until the
            principal resolves, and a grid whose item count changes would move
            every track behind it for one frame on every cold load.

            This is the one cluster whose separator travels INSIDE its slot —
            it is drawn by UserBadge itself. The account string is the only
            field on the bar whose width is a property of the person signed in
            rather than of the data, and a hairline stranded 60px behind a
            short address reads as a broken control; drawing it on the badge
            also means it goes when the badge does, rather than leaving a bare
            rule at the bezel while the principal is still resolving. The slot
            is fixed, so nothing else on the bar can feel any of it. */}
        <div className={styles.userSlot}>
          <UserBadge />
        </div>
      </header>

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onNavigate={navigate}
      />
    </>
  );
}
